// Reliable printing — both in a desktop browser and inside the Capacitor
// (Android / iOS) WebView.
//
// IMPORTANT: `window.print()` is a no-op inside the native WebViews
// (Android System WebView and iOS WKWebView don't implement it), so the old
// hidden-iframe + window.print() approach silently did nothing on device.
//
//   • Native (Android/iOS): print through the native print dialog via the
//     Capacitor printer plugin. The CSS we pass is embedded inline in the
//     document, so Android (which renders the HTML in a real WebView) prints it
//     fully styled. iOS renders simpler formatting.
//   • Web: keep the hidden same-document iframe (popup-blocker safe, no zoom).
//
// @param {string} bodyHtml  Inner HTML of the printable region.
// @param {string} title     Document title shown in the print dialog.
// @param {string} css       Optional `<style>…</style>` block to apply.
import { Capacitor } from '@capacitor/core'
import { Printer } from '@bcyesil/capacitor-plugin-printer'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { MEDIA_HOST, PICTURE_HOST } from '@/config/hosts'

function buildDocument(bodyHtml, title, css) {
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
    '<title>' +
    title +
    '</title>' +
    css +
    '</head><body>' +
    bodyHtml +
    '</body></html>'
  )
}

// ── Paper sizing policy ──────────────────────────────────────────────────────
// The SHEET is A4 portrait — that is what the printer is fed. The BILL is an
// 8cm × 21cm area centred left-to-right on that sheet and sitting 0.5in from its
// top edge (BILL_TOP_OFFSET — the same top margin every other print format in
// the app gets). Inside the bill area BILL_PADDING is taken off, leaving
// 7.6cm × 20.4cm of content.
//
// Declaring `@page{size:8cm 21cm}` instead is what NOT to do: the printer still
// feeds A4, and the mismatch drops the bill in a corner of the sheet (it did
// exactly that, and before then the same mismatch cropped the bottom off every
// slip when the page was declared 3.5in × 8in). The page size must match the
// paper; the slip dimensions belong to the block drawn on it.
//
// Reports still print A4 landscape, and GST invoices A4 portrait.
const BILL_PAPER_W = '8cm'
const BILL_PAPER_H = '21cm'
// Top gap on EVERY print format in the app — half an inch of clear paper above
// the content, so pre-printed letterheads and the printer's own grip area never
// land on top of it. A full inch was tried first and read as too much white
// space at the head of the sheet. Single source of truth: PAGE_MARGIN below and
// the bill's BILL_TOP_OFFSET both read from here.
const PAGE_TOP_MARGIN = '0.5in'
// Extra gap ABOVE the bill area. The bill draws its own 21cm block on the sheet
// and PAGE_BILL_STYLE zeroes the @page margin, so this body padding — not
// PAGE_MARGIN — is what puts the required half inch above it. 0.5in + 21cm =
// 22.3cm, still inside A4's 29.7cm, so nothing spills onto a second sheet.
// Set this to '4.35cm' — (29.7cm − 21cm) / 2 — to centre it vertically instead.
const BILL_TOP_OFFSET = PAGE_TOP_MARGIN
// Inner padding of the bill area itself. Deliberately NOT PAGE_MARGIN: that
// constant now carries the 0.5in top, and half an inch of padding inside a
// block of fixed 21cm height would eat the slip's content box and push the tail
// (signature / photo grid) off the bottom — the exact failure BILL_PAPER_H's
// note describes. The gap above the bill comes from BILL_TOP_OFFSET instead.
const BILL_PADDING = '0.3cm 0.2cm 0.3cm 0.2cm'
// Paper margin applied to every MULTI-page print job (reports, A4 invoices,
// catalogues, barcode sheets). Top is PAGE_TOP_MARGIN; the other three sides
// stay at the printer-safe minimum — the 0.5cm sides they once had wasted
// paper width that fields like "At Time of Delivery" needed to stay on one
// line.
export const PAGE_MARGIN = PAGE_TOP_MARGIN + ' 0.2cm 0.3cm 0.2cm'

// PAGE_MARGIN as body padding, for MULTI-page documents (reports, A4 invoice
// pages). Those keep their @page margin — it is the only rule that margins
// EVERY page, and the browser honours it — so on the web this adds nothing and
// the gap comes from @page alone. On device the print WebView ignores @page
// (see PAGE_BILL_STYLE), so the padding stands in for it; that only margins the
// first page, but a partial margin beats none. Evaluated per call rather than
// at module load so it cannot run before the Capacitor bridge is ready.
export function bodyPad() {
  return Capacitor.isNativePlatform()
    ? 'body{padding:' + PAGE_MARGIN + '!important}'
    : ''
}

const PAGE_BILL_STYLE =
  '<style>' +
  // A4 sheet with a zero page margin, so the bill's position on it is set here
  // in ordinary layout rather than by @page — which the Android/iOS print
  // WebViews ignore outright (both `size` and `margin`; the OS print framework
  // owns the paper there). Everything below is plain CSS and so survives on
  // device as well as in the browser.
  '@page{size:A4 portrait;margin:0}' +
  'html{margin:0!important;padding:0!important}' +
  // BILL_TOP_OFFSET is the whole gap to the sheet's top edge — half an inch, the
  // same top margin every other print format gets. Padding rather than margin,
  // so it cannot collapse into the child.
  'body{margin:0!important;padding:' + BILL_TOP_OFFSET + ' 0 0!important}' +
  // The bill area itself: 8cm wide, centred left-to-right by the auto side
  // margins, with BILL_PADDING as its inner padding (border-box, so the padding
  // eats into the 8cm rather than widening it). Content lands at 7.6cm across.
  // This !important padding deliberately supersedes the `.slip{padding:…}` rule
  // each slip builder carries — that rule only applies if one is ever printed
  // standalone, without this stylesheet injected over it.
  'body>*{box-sizing:border-box!important;' +
  'width:' + BILL_PAPER_W + '!important;max-width:' + BILL_PAPER_W + '!important;' +
  'margin:0 auto!important;padding:' + BILL_PADDING + '!important}' +
  // The full 21cm height is applied only when the bill is a single block — the
  // client-composed slips. A server-rendered bill page has many body children,
  // and giving each of those a full slip would stack them down several sheets.
  'body>*:only-child{height:' + BILL_PAPER_H + '!important}' +
  '</style>'
// Report pages: A4 landscape with the standard PAGE_MARGIN.
// Rows stay on a single line (small font is fine), borders are light and the
// text dark + bold for readability. !important so these beat the per-page
// print CSS each report passes along.
const pageReportStyle = () =>
  '<style>' +
  '@page{size:A4 landscape;margin:' + PAGE_MARGIN + '}' +
  'body{margin:0!important}' +
  bodyPad() +
  'table{width:100%!important;border-collapse:collapse!important}' +
  'table th,table td{' +
  'border:1px solid #c9cfd8!important;' +
  'color:#111!important;' +
  'font-weight:700!important;' +
  'font-size:9.5px!important;' +
  'padding:3px 5px!important;' +
  'white-space:nowrap!important;' +
  '}' +
  'table th{background:#f2f4f8!important}' +
  '</style>'

// Full A4 portrait documents rendered server-side (GST invoices): the server's
// own layout is left alone, only the paper margin and the centring are ours.
const pageDocStyle = () =>
  '<style>' +
  '@page{size:A4 portrait;margin:' + PAGE_MARGIN + '}' +
  bodyPad() +
  'body>*{margin-left:auto!important;margin-right:auto!important}' +
  '</style>'

// Fetch a product/cloth image THROUGH the app and return it as a base64 data:
// URI so the (sessionless) native print WebView can show it. Two problems the
// print WebView has that this solves:
//   • session-protected images (www/mto product photos) — an <img> in the
//     print view has no cookies, so they come out blank;
//   • cross-origin fetch is CORS-blocked, so we fetch the SAME-ORIGIN relative
//     path (/Pictures/…, /mto/…) which axios sends to the app origin WITH the
//     session cookie and no CORS.
// Returns '' on any failure — the caller then falls back to a plain <img>.
// /Pictures/ is served by TWO different hosts and the path alone does not say
// which. The catalogue images the desktop system uploaded are on PICTURE_HOST;
// the photos this app captures are written into the Laravel site's own
// public/Pictures by /api/admin/upload-measurement-image, so they are on
// MEDIA_HOST. Printing hard-wired every /Pictures/ path to PICTURE_HOST, so a
// measurement photo that the SCREEN displayed fine (the screen builds its URL
// from MEDIA_HOST) 404'd on the slip and left the photo box blank.
//
// Return both, most-likely first: C_/T_/B_/P_/F_ followed by digits is the
// measurement naming (prefix + measurement id), everything else is catalogue.
export function pictureUrlCandidates(rel) {
  const hosts = /^\/pictures\/[ctbpf]_\d+\./i.test(rel)
    ? [MEDIA_HOST, PICTURE_HOST]
    : [PICTURE_HOST, MEDIA_HOST]
  return hosts.map((h) => h + rel)
}

export async function toDataUri(url) {
  if (!url) return ''
  let u = String(url)
  if (u.startsWith('data:')) return u

  // Same-origin path (drop the host): works on Android, where the WebView
  // fetch is CORS-restricted, IF the app origin serves the image.
  const rel0 = u.replace(/^https?:\/\/[^/]+/i, '')
  const rel = rel0.startsWith('/') ? rel0 : '/' + rel0
  // Full public image URL(s): works on iOS, where CapacitorHttp routes through
  // native URLSession (no CORS) and shares the session cookie jar.
  let absUrls = [u]
  if (!/^https?:\/\//i.test(u)) {
    absUrls = rel.toLowerCase().startsWith('/pictures/')
      ? pictureUrlCandidates(rel)
      : [MEDIA_HOST + rel]
  }

  // Try the absolute URL(s) first (iOS native HTTP), then the same-origin path
  // (Android). Whichever returns image bytes wins.
  const candidates = [...new Set([...absUrls, rel])]
  for (const c of candidates) {
    try {
      const res = await window.axios.get(c, {
        responseType: 'arraybuffer',
        headers: { 'Cache-Control': 'no-cache' },
      })
      const bytes = new Uint8Array(res.data)
      if (!bytes.length) continue
      const type = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || 'image/jpeg'
      // Guard against an HTML error page being returned as "image".
      if (String(type).indexOf('image') === -1 && String(type).indexOf('octet-stream') === -1) continue
      let bin = ''
      const CHUNK = 0x8000
      for (let i = 0; i < bytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
      }
      return 'data:' + (String(type).indexOf('image') !== -1 ? type : 'image/jpeg') + ';base64,' + btoa(bin)
    } catch (_) { /* try next candidate */ }
  }
  return ''
}

// Inject a page-style block into a fetched HTML document (before </head> when
// present, otherwise prepended) so the print engine gets the paper rules.
function injectPageStyle(html, styleTag) {
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, styleTag + '</head>')
  }
  return styleTag + html
}

// Print a FULL HTML document string the caller already has (e.g. the response
// of a bulk-print POST). Native: styled + printed via the plugin; web: opened
// as a blob in a new tab. `page`: 'bill' (8cm roll) or 'a4' (landscape report).
export async function printHtmlDocument(fullHtml, title = 'Report', page = 'bill') {
  const styled = injectPageStyle(String(fullHtml), page === 'a4' ? pageReportStyle()
    : page === 'a4doc' ? pageDocStyle()
    : PAGE_BILL_STYLE)
  if (Capacitor.isNativePlatform()) {
    try {
      await Printer.print({
        content: styled,
        name: title,
        orientation: page === 'a4' ? 'landscape' : 'portrait',
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Native print failed', e)
    }
    return
  }
  // Print in place. The old code opened the HTML as a blob: tab, which on iOS
  // Safari just shows the page with no print dialog (and the tab is popup-
  // blocked anyway, since we arrive here after an await).
  printHtmlInFrame(styled)
}

// Print a full HTML document through a same-document iframe — no new tab, no
// user gesture needed. The frame is kept off-screen at a REAL size rather than
// 0×0/visibility:hidden: iOS Safari ignores a hidden frame and prints the
// parent page instead of the frame's content.
function printHtmlInFrame(html) {
  const prev = document.getElementById('__print_frame__')
  if (prev && prev.parentNode) prev.parentNode.removeChild(prev)

  const iframe = document.createElement('iframe')
  iframe.id = '__print_frame__'
  iframe.setAttribute('aria-hidden', 'true')
  iframe.setAttribute('tabindex', '-1')
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:800px;height:1200px;border:0;opacity:0;pointer-events:none;'
  document.body.appendChild(iframe)

  const doc = iframe.contentWindow.document
  doc.open()
  doc.write(html)
  doc.close()

  let printed = false
  const fire = () => {
    if (printed) return
    printed = true
    const win = iframe.contentWindow
    try {
      win.focus()
      win.print()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Print failed', e)
    }
    // Safari's print() returns immediately, so keep the frame around long
    // enough for the dialog to capture it; the next print replaces it anyway.
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
    }, 60000)
  }

  // Print once the frame (and its images) have loaded; the timeout is the
  // safety net for browsers that don't fire onload after document.write.
  iframe.onload = () => setTimeout(fire, 300)
  setTimeout(fire, 1500)
}

// The SPA's own backend pages (/api/...), which need the API token.
function isAppApiUrl(url) {
  return /^\/api\//.test(String(url || ''))
}

// Print a server-rendered print page (e.g. /api/admin/sale/orderform-print-html).
// The old code did window.open(url), which is blocked/broken inside the
// Android & iOS WebViews — the page opened blank or not at all. Instead fetch
// the HTML (same-origin, session cookies included) and hand it to the native
// print dialog; on desktop web keep the plain new-tab behaviour.
// `page`: 'bill' (default — 8cm x 21cm slip) or 'a4' (A4 landscape).
export async function printServerHtml(url, title = 'Bill', page = 'bill') {
  if (!Capacitor.isNativePlatform() && !isAppApiUrl(url)) {
    window.open(url, '_blank')
    return
  }
  if (!Capacitor.isNativePlatform()) {
    // The app's own /api/ pages need the API token, which a new tab doesn't
    // carry ("Unauthenticated") — fetch through axios and print in place.
    try {
      const { data } = await window.axios.get(url, {
        params: { _ts: Date.now() },
        responseType: 'text',
        transformResponse: [(d) => d],
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      })
      await printHtmlDocument(typeof data === 'string' ? data : String(data), title, page)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Print failed', e)
    }
    return
  }
  try {
    // Cache-bust: re-printing an invoice after editing hits the SAME url, and
    // the WebView would happily serve the stale cached HTML (old invoice).
    const fresh = url + (url.includes('?') ? '&' : '?') + '_ts=' + Date.now()
    const { data } = await window.axios.get(fresh, {
      responseType: 'text',
      // keep the raw HTML string — don't let axios try to JSON-parse it
      transformResponse: [(d) => d],
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
    let html = typeof data === 'string' ? data : String(data)
    // 'bill' → 8cm roll style; 'a4' → landscape report style;
    // 'a4doc' → A4 portrait with the server layout left untouched
    // (GST invoices are full A4 documents designed server-side).
    const style = page === 'a4' ? pageReportStyle()
      : page === 'a4doc' ? pageDocStyle()
      : PAGE_BILL_STYLE
    html = injectPageStyle(html, style)
    await Printer.print({
      content: html,
      name: title,
      orientation: page === 'a4' ? 'landscape' : 'portrait',
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Native print failed', e)
  }
}

// "Download" a server-generated PDF (e.g. /admin/sale/gstinvoice/pdf/{id}).
// window.open(url) can't download inside the app WebViews, so on device we
// fetch the PDF (same-origin, session cookies included), write it to the app
// cache, and open the OS share sheet — the user can save to Files/Drive, print,
// or send it. On desktop web the plain new-tab download is kept.
export async function downloadServerPdf(url, filename = 'document.pdf') {
  if (!Capacitor.isNativePlatform()) {
    if (!isAppApiUrl(url)) {
      window.open(url, '_blank')
      return
    }
    // A new tab carries no API token ("Unauthenticated"), so fetch the PDF
    // through axios and save it from a blob link instead.
    try {
      const res = await window.axios.get(url, {
        params: { _ts: Date.now() },
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      })
      const href = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = href
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(href), 60000)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('PDF download failed', e)
    }
    return
  }
  try {
    // Cache-bust so an edited invoice's PDF is regenerated, not served stale.
    const fresh = url + (url.includes('?') ? '&' : '?') + '_ts=' + Date.now()
    const res = await window.axios.get(fresh, {
      responseType: 'arraybuffer',
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
    // arraybuffer → base64 (chunked so large PDFs don't blow the call stack)
    const bytes = new Uint8Array(res.data)
    let bin = ''
    const CHUNK = 0x8000
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
    }
    const b64 = btoa(bin)
    const { uri } = await Filesystem.writeFile({
      path: filename,
      data: b64,
      directory: Directory.Cache,
    })
    await Share.share({ title: filename, url: uri, dialogTitle: filename })
  } catch (e) {
    // Dismissing the share sheet rejects with a benign "canceled" error.
    if (!(e && /cancel/i.test(e.message || ''))) {
      // eslint-disable-next-line no-console
      console.error('PDF download failed', e)
    }
  }
}

// Print a server-generated PDF page (the legacy billpdf/receiptpdf .aspx
// endpoints on the android. subdomain). The old code did window.open(url),
// which on iOS just opened a web page with no Print option and on Android left
// the app. Instead: fetch the document (the server sends
// Access-Control-Allow-Origin:*, so the cross-origin GET works) and hand it to
// the native print dialog — Wi-Fi printers show up there on both platforms.
export async function printServerPdf(url, title = 'Bill') {
  // iOS ATS blocks cleartext http:// and the host supports https.
  const secure = url.replace(/^http:\/\//i, 'https://')
  if (!Capacitor.isNativePlatform()) {
    window.open(secure, '_blank')
    return
  }
  try {
    const fresh = secure + (secure.includes('?') ? '&' : '?') + '_ts=' + Date.now()
    const res = await window.axios.get(fresh, {
      responseType: 'arraybuffer',
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
    const type = String(res.headers && res.headers['content-type'] || '')
    const bytes = new Uint8Array(res.data)
    if (type.includes('pdf')) {
      // PDF bytes → base64 → native print (both plugin platforms handle
      // the "base64:" content prefix).
      let bin = ''
      const CHUNK = 0x8000
      for (let i = 0; i < bytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
      }
      await Printer.print({ content: 'base64:' + btoa(bin), name: title })
    } else {
      // The endpoint answered with an HTML page (legacy billpdf/receiptpdf
      // layout). Keep the server's design intact, but print PORTRAIT with the
      // fixed-width bill centred on the page instead of hugging a corner.
      const centre =
        '<style>' +
        '@page{size:A4 portrait;margin:' + PAGE_MARGIN + '}' +
        'body{margin:0!important}' +
        // This branch is native-only, and the print WebView ignores @page —
        // the padding is what actually produces the margin here.
        bodyPad() +
        'body>*{margin-left:auto!important;margin-right:auto!important}' +
        '</style>'
      const html = injectPageStyle(new TextDecoder('utf-8').decode(bytes), centre)
      await Printer.print({ content: html, name: title, orientation: 'portrait' })
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Native PDF print failed', e)
  }
}

// arraybuffer/bytes → base64 (chunked so large PDFs don't blow the call stack)
function bytesToBase64(bytes) {
  let bin = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
  }
  return btoa(bin)
}

// Print a PDF blob on the WEB without tripping the popup blocker.
//
// window.open() only survives the blocker while the browser still credits the
// call to a user gesture. Every caller here reaches this point from a promise
// — html2pdf().output('blob').then(...), or an axios response — by which time
// the gesture has expired, so Chrome and Edge silently swallowed the tab and
// nothing printed. A same-document iframe needs no gesture at all, which is
// why printHtml() below has always used one.
//
// The frame is left in the DOM while the print dialog is open (removing it
// cancels the job) and cleaned up on the next print or when the page unloads.
function printBlobInFrame(blob, title = 'Report') {
  const prev = document.getElementById('__print_pdf_frame__')
  if (prev) {
    if (prev.dataset.objectUrl) URL.revokeObjectURL(prev.dataset.objectUrl)
    if (prev.parentNode) prev.parentNode.removeChild(prev)
  }

  const objectUrl = URL.createObjectURL(blob)
  const iframe = document.createElement('iframe')
  iframe.id = '__print_pdf_frame__'
  iframe.title = title
  iframe.dataset.objectUrl = objectUrl
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;'

  let printed = false
  const fire = () => {
    if (printed) return
    printed = true
    try {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
    } catch (e) {
      // Some browsers refuse to drive the built-in PDF viewer from script.
      // A new tab is the only fallback left; if the blocker eats it, the user
      // gets the browser's own "popup blocked" prompt rather than silence.
      // eslint-disable-next-line no-console
      console.error('PDF frame print failed, opening in a tab instead', e)
      window.open(objectUrl, '_blank')
    }
  }

  iframe.onload = () => setTimeout(fire, 300)
  document.body.appendChild(iframe)
  iframe.src = objectUrl
  // The viewer can take a moment to parse a big report; onload is the normal
  // trigger, this is only the safety net.
  setTimeout(fire, 2000)
}

// Print a PDF the page has ALREADY fetched as a Blob (the cutting/finish/MTO
// screens fetch report PDFs with axios then did window.open(objectUrl) — a
// no-op in the app WebViews). Native: hand the bytes to the printer plugin
// (both platforms support `base64:` content). Web: hidden iframe, see below.
export async function printPdfBlob(blob, title = 'Report') {
  if (!Capacitor.isNativePlatform()) {
    printBlobInFrame(blob, title)
    return
  }
  try {
    const buf = await blob.arrayBuffer()
    const b64 = bytesToBase64(new Uint8Array(buf))
    await Printer.print({ content: 'base64:' + b64, name: title })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Native PDF print failed', e)
  }
}

export function printHtml(bodyHtml, title = 'Report', css = '', orientation = '') {
  // Reports default to A4 landscape with no side margins. A caller that sets
  // its own @page rule (e.g. the Stock Report's explicit portrait) wins.
  if (!/@page/i.test(css)) {
    css = pageReportStyle() + css
    if (!orientation) orientation = 'landscape'
  }
  const html = buildDocument(bodyHtml, title, css)

  // ── Native (Android / iOS) ─────────────────────────────────────────────
  if (Capacitor.isNativePlatform()) {
    const job = { content: html, name: title }
    // 'portrait' | 'landscape' — both plugin platforms honour it.
    if (orientation) job.orientation = orientation
    Printer.print(job).catch((e) => {
      // eslint-disable-next-line no-console
      console.error('Native print failed', e)
    })
    return
  }

  // ── Web (desktop browser / iOS Safari) ─────────────────────────────────
  printHtmlInFrame(html)
}

// POPIN logo for the slip header, embedded as a data URI so the print
// WebView never depends on the network to show it (public/theme/images/logo.png).
const POPIN_LOGO =
  'data:image/png;base64,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'


// ── Invoice slip (Store / MTO / Rental bill) ─────────────────────────────────
// Client-composed bill following the "NEW BILL SLIP 2026" model (page 1 —
// INVOICE), printed on the 8cm x 21cm slip paper. Replaces the old server
// print pages (orderform-print-html / mto-orderform-print-html / billpdf.aspx)
// whose layout can't be changed from the app.
export async function printInvoiceSlip(info = {}) {
  const esc = (v) =>
    String(v === null || v === undefined ? '' : v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  // Amounts print as ₹4,000 (rupee sign + Indian digit grouping). A blank
  // value stays blank so the padded item rows don't fill up with ₹0.
  const money = (v) => {
    if (v === undefined || v === null || String(v).trim() === '') return ''
    const n = Number(v)
    return isNaN(n) ? String(v) : '₹' + n.toLocaleString('en-IN')
  }
  // The totals block always shows a figure, including zero — the client wants
  // Paid and Balance on the bill even when nothing has been paid yet.
  const moneyZ = (v) => money(v === undefined || v === null || String(v).trim() === '' ? 0 : v)
  let coname = ''
  let branchname = ''
  try {
    const ses = JSON.parse(localStorage.getItem('popin_session') || 'null') || {}
    coname = ses.coname || ses.Description || ''
    branchname = ses.branchname || ses.branch_name || ''
  } catch (_) { /* header shows the company name only */ }

  const hdr = await getCompanyHeader()
  const contact =
    (hdr.address ? '<div class="adr">' + esc(hdr.address) + '</div>' : '') +
    (hdr.phone || hdr.email
      ? '<div class="adr">' +
        [hdr.phone ? 'Ph. : ' + esc(hdr.phone) : '', hdr.email ? 'Email : ' + esc(hdr.email) : '']
          .filter(Boolean)
          .join(' | ') +
        '</div>'
      : '')

  // Item rows padded to 5 like the model
  const items = Array.isArray(info.items) ? info.items : []
  // Some MTO bills carry multiple product names in one line (comma/newline
  // separated). Print each product on its own row while keeping financial
  // totals unchanged by only showing amount columns on the first split row.
  const splitItemNames = (name) =>
    String(name === undefined || name === null ? '' : name)
      .split(/[\r\n,;|]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  const displayItems = items.flatMap((it) => {
    const names = splitItemNames(it && it.name)
    if (names.length <= 1) {
      return [{ ...(it || {}), name: names[0] || (it && it.name) || '' }]
    }
    return names.map((name, idx) => ({
      ...(it || {}),
      name,
      code: idx === 0 ? (it && it.code) : '',
      size: idx === 0 ? (it && it.size) : '',
      qty: idx === 0 ? (it && it.qty) : '',
      rate: idx === 0 ? (it && it.rate) : '',
      amount: idx === 0 ? (it && it.amount) : '',
    }))
  })
  let rows = ''
  for (let i = 0; i < Math.max(5, displayItems.length); i++) {
    const it = displayItems[i] || {}
    rows +=
      '<tr><td class="c">' + esc(it.code) + '</td><td>' + esc(it.name) +
      '</td><td class="c">' + esc(it.size) + '</td><td class="c">' + esc(it.qty) +
      '</td><td class="r">' + money(it.rate) + '</td><td class="r">' + money(it.amount) + '</td></tr>'
  }

  // Product photos at the bottom of the bill (item code above each photo).
  // Embedded as same-origin data URIs, falling back to a plain <img>.
  const abs = (u) => {
    if (!u) return ''
    u = String(u)
    if (u.startsWith('data:') || u.startsWith('http://') || u.startsWith('https://')) return u
    if (!u.startsWith('/')) u = '/' + u
    if (u.toLowerCase().startsWith('/pictures/')) return 'https://popin.popinweddingdesigner.com' + u
    return MEDIA_HOST + u
  }
  // Limit to at most 3 photos on the slip (client requested). Center them
  // and pick reasonable heights for 1..3 photos.
  const withPhotos = items.filter((it) => it && it.photo)
  const photosToShow = withPhotos.slice(0, 3)
  const cols = photosToShow.length === 1 ? 1 : photosToShow.length === 2 ? 2 : 3
  const photoH = photosToShow.length === 1 ? 1.0 : photosToShow.length === 2 ? 0.8 : 0.7
  // Item names wrap to a second line in the narrow Item Name column, so a long
  // bill's table grows faster than one row per item — drop a point of font to
  // keep it inside the print area.
  const itemFont = displayItems.length > 8 ? 8.5 : 9.5
  const photoUris = await Promise.all(photosToShow.map((it) => toDataUri(it.photo)))
  const photoCells = photosToShow
    .map((it, i) => {
      const src = photoUris[i] || abs(it.photo)
      return '<div class="pgc"><div class="pgh">' + esc(it.code) + '</div><div class="pgp">' +
        (src ? '<img src="' + esc(src) + '" onerror="this.style.display=\'none\'">' : '') +
        '</div></div>'
    })
    .join('')

  const totalRow = (label, value, strong) =>
    '<tr><td class="tl' + (strong ? ' big' : '') + '">' + label + ' :</td><td class="tv' +
    (strong ? ' big' : '') + '">' + moneyZ(value) + '</td></tr>'

  // Trial / Delivery dates — large font inside their own box (per client).
  const dateRows = (info.dates || [])
    .filter((d) => d && d[1])
    .map((d) => '<div class="dtl"><b>' + esc(d[0]) + ' :</b> ' + esc(d[1]) + '</div>')
    .join('')
  const dateLines = dateRows ? '<div class="dtbx">' + dateRows + '</div>' : ''

  const terms = info.terms || [
    'If any complaints it should be given in writing within 24 hours.',
    'Goods once sold will not be taken back or exchanged.',
    'Please collect your order on or before delivery date.',
  ]

  const html =
    '<!doctype html><html><head><meta charset="utf-8"><title>' + esc(info.title || 'Invoice') + '</title>' +
    '<style>' +
    '*{box-sizing:border-box}' +
    // Noto Sans / Roboto are listed ahead of Helvetica because the ₹ sign
    // (U+20B9) is missing from some older Arial builds and would print as a box.
    'body{font-family:Arial,"Noto Sans",Roboto,Helvetica,sans-serif;color:#000;font-size:10px;margin:0}' +
    // No top padding: the shared PAGE_BILL_STYLE already leaves BILL_TOP_OFFSET's
    // top gap at the top of the sheet (as body padding). Side/bottom padding
    // stays as inner breathing room inside the slip block that style pins on
    // body>*.
    '.slip{width:100%;padding:0 0.15in 0.08in}' +
    '.lg img{display:block;margin:0 auto}' +
    '.inv{font-size:12px;font-weight:bold;text-align:center;text-decoration:underline;letter-spacing:1px;margin:2px 0 4px}' +
    '.lg{text-align:center;margin:0 0 2px}' +
    '.lg img{height:0.3in}' +
    '.co{font-size:15px;font-weight:bold;text-align:center;letter-spacing:1px}' +
    '.brn{font-size:10.5px;font-weight:bold;text-align:center;border-top:2px solid #000;margin-top:2px;padding-top:2px;margin-bottom:3px}' +
    '.adr{font-size:8.5px;font-weight:bold;text-align:center;margin:0}' +
    // Kept tight so the bill stays inside the 19.5cm print area even when it
    // runs to a dozen items — each item costs a table row AND a photo cell.
    '.bx{border:1.5px solid #000;padding:4px 6px;margin:0 0 5px}' +
    'table{width:100%;border-collapse:collapse}' +
    '.inf td{padding:3px 0;font-size:10px}' +
    '.inf .l{font-weight:900;white-space:nowrap;width:17%}' +
    '.inf .v{font-weight:900;width:33%}' +
    '.it th{border:1px solid #000;font-size:9px;padding:3px 2px;text-align:center}' +
    '.it td{border:1px solid #000;height:18px;padding:2px 3px;font-weight:bold;font-size:' + itemFont + 'px}' +
    '.it td.c{text-align:center}' +
    '.it td.r{text-align:right}' +
    '.tt{margin:0 0 4px}' +
    '.tt .tl{border:1px solid #000;font-weight:900;text-align:right;padding:4px 5px;width:72%;white-space:nowrap}' +
    '.tt .tv{border:1px solid #000;font-weight:900;text-align:right;padding:4px 5px;white-space:nowrap}' +
    '.tt .big{font-size:13px;font-weight:900}' +
    '.dtl{font-weight:900;font-size:12px;margin:1px 0}' +
    '.dtbx{border:1.5px solid #000;padding:3px 6px;margin:3px 0 3px}' +
    '.dash{border-top:1.5px dashed #000;margin:4px 0 3px}' +
    '.trm{font-size:9px}' +
    '.trm b{font-size:9.5px}' +
    '.trm div{margin:0}' +
    '.sig{border:1.5px solid #000;margin-top:3px;padding:3px 6px;height:0.38in;display:flex;flex-direction:column;justify-content:space-between}' +
    '.pg{display:flex;flex-wrap:nowrap;justify-content:center;gap:6px;margin-top:5px}' +
    '.pgc{width:calc(' + (100 / cols).toFixed(2) + '% - 6px);page-break-inside:avoid;break-inside:avoid;min-width:0}' +
    '.pgh{border:1.5px solid #000;border-bottom:none;font-weight:bold;text-align:center;padding:1px 0;font-size:' + (cols > 3 ? 8.5 : 9.5) + 'px}' +
    '.pgp{border:1.5px solid #000;height:' + photoH + 'in;display:flex;align-items:center;justify-content:center;overflow:hidden}' +
    '.pgp img{max-width:100%;max-height:100%;display:block}' +
    '.sig .for{font-weight:bold;text-align:right}' +
    '.sig .auth{text-align:right}' +
    '</style></head><body><div class="slip">' +
    '<div class="lg"><img src="' + POPIN_LOGO + '"></div>' +
    '<div class="co">' + esc(coname || 'POPIN') + '</div>' +
    '<div class="brn">' + contact + '</div>' +
    '<div class="inv">' + esc(info.heading || 'INVOICE') + '</div>' +
    '<div class="bx"><table class="inf">' +
    '<tr><td class="l">Order No. :</td><td class="v">' + esc(info.orderNo) + '</td><td class="l">Date :</td><td class="v">' + esc(info.date) + '</td></tr>' +
    // Name reads as one phrase — "Ms. NAMEETA THACKER" — rather than an "M/s."
    // label tabbed away from the name in the next column.
    '<tr><td class="v" colspan="2">Ms. ' + esc(info.name) + '</td><td class="l">Mob. :</td><td class="v">' + esc(info.mobile) + '</td></tr>' +
    // Ref. and Tailor Master dropped at the client's request; Salesman now runs
    // the width of the box.
    '<tr><td class="l">Salesman :</td><td class="v" colspan="3">' + esc(info.salesman) + '</td></tr>' +
    '</table></div>' +
    '<table class="it"><tr><th style="width:12%">Code</th><th style="width:32%">Item Name</th><th style="width:11%">Size</th><th style="width:9%">Qty.</th><th style="width:17%">Rate</th><th style="width:19%">Amount</th></tr>' +
    rows +
    '</table><div style="height:6px"></div>' +
    '<table class="tt">' +
    totalRow('TOTAL', info.total) +
    totalRow('Discount', info.discount) +
    totalRow('Grand Total', info.grandTotal) +
    totalRow('Paid Amount', info.paid) +
    totalRow('Balance Amount', info.balance, true) +
    '</table>' +
    dateLines +
    (photoCells ? '<div class="pg">' + photoCells + '</div>' : '') +
    '<div class="dash"></div>' +
    '<div class="trm"><b>Terms :</b>' +
    terms.map((t, i) => '<div>' + (i + 1) + ')&nbsp; ' + esc(t) + '</div>').join('') +
    '</div>' +
    '<div class="sig"><div class="for">For ' + esc(coname || 'POPIN') + '</div><div class="auth">Authorised Signatory</div></div>' +
    '</div></body></html>'

  await printHtmlDocument(html, info.title || 'Invoice', 'bill')
}

// ── Rental Booking Receipt ───────────────────────────────────────────────────
// Client-composed Booking Receipt following the "NEW BILL SLIP 2026" model
// (page 2), printed on the 8cm x 21cm slip paper. Replaces the server PDF,
// which still used the old layout.
export async function printBookingReceiptSlip(info = {}) {
  const esc = (v) =>
    String(v === null || v === undefined ? "" : v)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  // Photos must load from the PUBLIC image hosts — the print WebView has no
  // app session (see printJobWorkSlip's abs for details).
  const abs = (u) => {
    if (!u) return ""
    u = String(u)
    if (u.startsWith("data:") || u.startsWith("http://") || u.startsWith("https://")) return u
    if (!u.startsWith("/")) u = "/" + u
    if (u.toLowerCase().startsWith("/pictures/")) return "https://popin.popinweddingdesigner.com" + u
    return MEDIA_HOST + u
  }
  // Times are stored as plain hour numbers (14 -> "2.00 p.m."); anything
  // already formatted passes through untouched.
  const fmtTime = (t) => {
    if (t === undefined || t === null || String(t).trim() === "") return ""
    const str = String(t).trim()
    if (/[ap].?m/i.test(str) || str.includes(":")) return str
    const n = parseFloat(str)
    if (isNaN(n)) return str
    const h = Math.floor(n)
    const mins = Math.round((n - h) * 100)
    const suffix = h >= 12 ? "p.m." : "a.m."
    const h12 = h % 12 === 0 ? 12 : h % 12
    return h12 + "." + ("0" + mins).slice(-2) + " " + suffix
  }
  // Amounts print WITHOUT a trailing ".00". The item rents arrive straight off
  // the booking rows, where the API sends a decimal string ("1500.00"), so the
  // column printed the empty paise. A genuine paise value (1500.50) is kept.
  // Blank stays blank so the padded item rows don't fill with zeros.
  const money = (v) => {
    if (v === undefined || v === null || String(v).trim() === "") return ""
    const n = Number(v)
    return isNaN(n) ? String(v) : n.toFixed(2).replace(/\.00$/, "")
  }
  let coname = ""
  let branchname = ""
  try {
    const ses = JSON.parse(localStorage.getItem("popin_session") || "null") || {}
    coname = ses.coname || ses.Description || ""
    branchname = ses.branchname || ses.branch_name || ""
  } catch (_) { /* header shows the company name only */ }

  const hdr = await getCompanyHeader()
  const contact =
    (hdr.address ? '<div class="adr">' + esc(hdr.address) + '</div>' : '') +
    (hdr.phone || hdr.email
      ? '<div class="adr">' +
        [hdr.phone ? 'Ph. : ' + esc(hdr.phone) : '', hdr.email ? 'Email : ' + esc(hdr.email) : '']
          .filter(Boolean)
          .join(' | ') +
        '</div>'
      : '')

  // Item rows padded to 6 like the model
  const items = Array.isArray(info.items) ? info.items : []
  // A long booking adds a row per item AND a photo row per 3 items, which is
  // what pushes the slip off the bottom of the sheet. Past 8 items the rows
  // tighten so even a 12-item booking finishes inside the printable height.
  // Both heights were raised (13/11 -> 18/15) to open the item table up; that
  // costs ~5px a row, so a 12-item booking is now the case to watch.
  const rowH = items.length > 8 ? 15 : 18
  let rows = ""
  for (let i = 0; i < Math.max(6, items.length); i++) {
    const it = items[i] || {}
    rows +=
      // 4th column = the line's DISCOUNT amount (the model's unlabelled column
      // between Rent and Remarks). It used to print the quantity, which was
      // hard-coded to 1 on every row, so a discounted booking showed no sign of
      // its discount anywhere on the slip.
      "<tr><td class=\"c\">" + (i + 1) + "</td><td>" + esc(it.code) +
      "</td><td class=\"c\">" + esc(money(it.rent)) + "</td><td class=\"c\">" +
      esc(money(it.disc)) + "</td><td>" + esc(it.remark) + "</td></tr>"
  }

  // Photo grid: item code above each photo, three per row. Embed each photo as
  // a same-origin data URI; fall back to a plain <img> at the public URL.
  const withPhotos = items.filter((it) => it && (it.photo || it.code))
  // The grid is the slip's biggest variable cost (~0.8in per row at the normal
  // size), so it is budgeted rather than left to grow: the usual 3-across x
  // 0.62in layout of the client's model is kept for the common bookings, and
  // only larger ones pack more photos per row in shorter boxes.
  const cols = withPhotos.length > 9 ? 5 : withPhotos.length > 6 ? 4 : 3
  const photoH = withPhotos.length > 9 ? 0.46 : withPhotos.length > 6 ? 0.56 : 0.78
  const withUris = await Promise.all(
    withPhotos.map((it) => (it.photo ? toDataUri(it.photo) : Promise.resolve('')))
  )
  const photoCells = withPhotos
    .map(
      (it, i) => {
        const src = withUris[i] || (it.photo ? abs(it.photo) : '')
        return "<div class=\"pgc\"><div class=\"pgh\">" + esc(it.code) + "</div><div class=\"pgp\">" +
        (src ? "<img src=\"" + esc(src) + "\" onerror=\"this.style.display=&#39;none&#39;\">" : "") +
        "</div></div>"
      }
    )
    .join("")

  const html =
    "<!doctype html><html><head><meta charset=\"utf-8\"><title>Booking Receipt</title>" +
    "<style>" +
    "*{box-sizing:border-box}" +
    // The slip must finish inside the printable height of the 8cm x 21cm
    // bill area — 20.4cm once BILL_PADDING is off — and printers reserve a further
    // unprintable strip at the bottom, so an overlong slip is sliced mid-row.
    // Every block below is kept tight (small logo, 1-2px cell padding, short
    // photo boxes) so the whole receipt lands well inside that budget.
    // Body padding is deliberately NOT reset here: the injected PAGE_BILL_STYLE
    // owns it (that is where the paper margin lives).
    "html{margin:0;padding:0}" +
    "body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#000;font-size:10px}" +
    // No top padding: the shared PAGE_BILL_STYLE already leaves BILL_TOP_OFFSET's
    // top gap at the top of the sheet (as body padding). Side/bottom padding
    // stays as inner breathing room inside the slip block that style pins on
    // body>*.
    ".slip{width:100%;padding:0 0.15in 0.06in}" +
    ".lg{text-align:center;margin-bottom:1px}" +
    ".lg img{height:0.28in}" +
    ".co{font-size:15px;line-height:1.1;font-weight:bold;text-align:center;letter-spacing:1px}" +
    ".brn{font-size:11px;font-weight:bold;text-align:center;margin-bottom:2px}" +
    ".adr{font-size:8.5px;font-weight:bold;text-align:center;margin:0}" +
    ".ttl{font-size:11.5px;font-weight:bold;text-align:center;margin:1px 0 5px}" +
    // Gap between the bordered boxes, and between the loose rows below them, so
    // the slip reads as separated blocks rather than one dense stack. Trimmed
    // from 14px — that spacing, stacked across 3 boxes plus the .adv/.chk rows
    // below, was the main thing pushing the photo grid at the bottom of a long
    // booking off the fixed-height page.
    ".bx{border:1.5px solid #000;padding:4px 6px;margin:0 0 8px}" +
    "table{width:100%;border-collapse:collapse}" +
    ".inf td{padding:3px 0;font-size:10px}" +
    ".inf .l{font-weight:900;white-space:nowrap;width:16%}" +
    ".inf .v{font-weight:900;width:34%}" +
    ".dts td{padding:3px 0;font-size:10.5px;font-weight:900}" +
    // white-space:nowrap on the label/value keeps "At Time of Delivery :" and
    // its amount from wrapping onto a second line — flex alone doesn't stop
    // long text inside a flex item from wrapping.
    ".adv{display:flex;align-items:center;gap:6px;margin:0 0 5px;white-space:nowrap}" +
    ".adv .al{font-weight:900;flex:1;font-size:12px;line-height:1.1;white-space:nowrap}" +
    ".adv .av{font-weight:900;width:82px;text-align:center;font-size:13px;line-height:1.1;white-space:nowrap}" +
    ".cash{border:1.5px solid #000;font-weight:900;padding:1px 6px;white-space:nowrap;font-size:11.5px}" +
    ".chk{font-weight:bold;margin:0 0 5px}" +
    ".ith{font-size:11px;font-weight:bold;text-align:center;border-bottom:1.5px solid #000;margin:-4px -6px 0;padding:2px 0}" +
    ".it{margin-top:0}" +
    ".it th{border:1px solid #000;font-size:9px;padding:3px 2px;text-align:center}" +
    ".it td{border:1px solid #000;height:" + rowH + "px;padding:2px 3px;font-weight:bold}" +
    ".it td.c{text-align:center}" +
    ".vt{margin-top:3px}" +
    ".vt td{border:1.5px solid #000;font-weight:900;padding:4px 5px}" +
    ".pg{display:flex;flex-wrap:wrap;gap:5px;margin-top:2px}" +
    // page-break-inside so a photo is never sliced in half if the grid ever
    // does spill past the sheet.
    ".pgc{width:calc(" + (100 / cols).toFixed(2) + "% - 4px);page-break-inside:avoid;break-inside:avoid}" +
    ".pgh{border:1.5px solid #000;border-bottom:none;font-weight:bold;text-align:center;padding:1px 0;font-size:" + (cols > 3 ? 8.5 : 9.5) + "px}" +
    ".pgp{border:1.5px solid #000;height:" + photoH + "in;display:flex;align-items:center;justify-content:center;overflow:hidden}" +
    ".pgp img{max-width:100%;max-height:100%}" +
    "</style></head><body><div class=\"slip\">" +
    "<div class=\"lg\"><img src=\"" + POPIN_LOGO + "\"></div>" +
    "<div class=\"co\">" + esc(coname || "POPIN") + "</div>" +
    "<div class=\"brn\">" + contact + "</div>" +
    "<div class=\"ttl\">Booking Receipt</div>" +
    "<div class=\"bx\"><table class=\"inf\">" +
    "<tr><td class=\"l\">No. :</td><td class=\"v\">" + esc(info.no) + "</td><td class=\"l\">Date :</td><td class=\"v\">" + esc(info.date) + "</td></tr>" +
    "<tr><td class=\"l\">Name :</td><td class=\"v\">" + esc(info.name) + "</td><td class=\"l\">Card No. :</td><td class=\"v\">" + esc(info.cardNo) + "</td></tr>" +
    "<tr><td class=\"l\">Mobile :</td><td class=\"v\">" + esc(info.mobile) + "</td><td class=\"l\">Salesman :</td><td class=\"v\">" + esc(info.salesman) + "</td></tr>" +
    "</table></div>" +
    "<div class=\"bx\"><table class=\"dts\">" +
    "<tr><td>Delivery Date :</td><td>" + esc(info.deliveryDate) + "</td><td style=\"text-align:right\">" + esc(fmtTime(info.deliveryTime)) + "</td></tr>" +
    "<tr><td>Return Date :</td><td>" + esc(info.returnDate) + "</td><td style=\"text-align:right\">" + esc(fmtTime(info.returnTime)) + "</td></tr>" +
    "</table></div>" +
    "<div class=\"adv\"><span class=\"al\">Advance Deposit :</span><span class=\"av\">" + esc(money(info.advanceDeposit)) + "</span><span style=\"width:70px\"></span></div>" +
    "<div class=\"adv\"><span class=\"al\">At Time of Delivery :</span><span class=\"av\">" + esc(money(info.atDelivery)) + "</span><span class=\"cash\">" + esc(info.payType || "CASH ONLY") + "</span></div>" +
    "<div class=\"chk\">Please check your Delivery Date and Time</div>" +
    "<div class=\"bx\"><div class=\"ith\">ITEM DETAILS</div>" +
    "<table class=\"it\"><tr><th style=\"width:11%\">Sr. No.</th><th style=\"width:30%\">Item No.</th><th style=\"width:14%\">Rent</th><th style=\"width:9%\"></th><th>Remarks</th></tr>" +
    rows +
    "</table>" +
    // Totals row, with each cell sitting under the item column it sums: Value
    // spans Sr.No + Item No., then the Rent total, then the Discount total, and
    // Total Rent (= Rent total - Discount total) on the right. The Discount
    // cell is new — without it the column above had nothing to add up to and
    // the discount never appeared on the slip.
    "<table class=\"vt\"><tr><td style=\"width:41%\">Value : <span style=\"float:right\">" + esc(money(info.totalValue)) + "</span></td>" +
    "<td style=\"width:14%;text-align:center\">" + esc(money(info.rentTotal)) + "</td>" +
    "<td style=\"width:9%;text-align:center\">" + esc(money(info.discTotal)) + "</td>" +
    "<td style=\"width:36%\">Total Rent : <span style=\"float:right\">" + esc(money(info.totalRent)) + "</span></td></tr></table>" +
    "</div>" +
    "<div class=\"pg\">" + photoCells + "</div>" +
    "</div></body></html>"

  await printHtmlDocument(html, "Booking Receipt", "bill")
}

// ── Rental Delivery Receipt ──────────────────────────────────────────────────
// Client-composed Delivery Receipt following the "NEW BILL SLIP 2026" model
// (page 3), printed on the 8cm x 21cm slip paper. Replaces the server
// receiptpdf.aspx PDF, which still used the old layout.
export async function printDeliveryReceiptSlip(info = {}) {
  const esc = (v) =>
    String(v === null || v === undefined ? "" : v)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  const abs = (u) => {
    if (!u) return ""
    u = String(u)
    if (u.startsWith("data:") || u.startsWith("http://") || u.startsWith("https://")) return u
    if (!u.startsWith("/")) u = "/" + u
    if (u.toLowerCase().startsWith("/pictures/")) return "https://popin.popinweddingdesigner.com" + u
    return MEDIA_HOST + u
  }
  const fmtTime = (t) => {
    if (t === undefined || t === null || String(t).trim() === "") return ""
    const str = String(t).trim()
    if (/[ap].?m/i.test(str) || str.includes(":")) return str
    const n = parseFloat(str)
    if (isNaN(n)) return str
    const h = Math.floor(n)
    const mins = Math.round((n - h) * 100)
    const suffix = h >= 12 ? "p.m." : "a.m."
    const h12 = h % 12 === 0 ? 12 : h % 12
    return h12 + "." + ("0" + mins).slice(-2) + " " + suffix
  }
  // Amounts print WITHOUT a trailing ".00" — the rents on these slips are whole
  // rupees and the client reads "1500", not "1500.00". A genuine paise value is
  // still shown in full (1500.50), so only the empty decimal part is dropped.
  const money = (v) => {
    if (v === undefined || v === null || String(v).trim() === "") return ""
    const n = Number(v)
    return isNaN(n) ? String(v) : n.toFixed(2).replace(/\.00$/, "")
  }
  let coname = ""
  try {
    const ses = JSON.parse(localStorage.getItem("popin_session") || "null") || {}
    coname = ses.coname || ses.Description || ""
  } catch (_) { /* header shows the company name only */ }

  const hdr = await getCompanyHeader()
  const contact =
    (hdr.address ? '<div class="adr">' + esc(hdr.address) + '</div>' : '') +
    (hdr.phone || hdr.email
      ? '<div class="adr">' +
        [hdr.phone ? 'Ph. : ' + esc(hdr.phone) : '', hdr.email ? 'Email : ' + esc(hdr.email) : '']
          .filter(Boolean)
          .join(' | ') +
        '</div>'
      : '')

  const items = Array.isArray(info.items) ? info.items : []
  const itemCodes =
    String(info.itemCodes || '').trim() ||
    items
      .map((it) => String((it && it.code) || '').trim())
      .filter(Boolean)
      .filter((code, idx, arr) => arr.indexOf(code) === idx)
      .join(', ')
  // Two different fields, two different sources:
  //
  //  • The header's "Card No. :" is the CUSTOMER's card (`cardNo`, from
  //    Contact.CARD_NO) — a NUMBER, so only the digits are kept. Digit runs
  //    keep their internal spaces/hyphens, so a grouped card
  //    (1234 5678 9012 3456) survives intact.
  //
  //  • The box at the foot is the GPay payment reference (`gpayNo`, the
  //    receipt page's "GPay / Card No." box), printed EXACTLY as typed
  //    ("Hitesh 9821212001"). It used to feed the header too, so the GPay
  //    number also appeared as the Card No.
  const cardNumberOnly = (v) => {
    const raw = String(v === null || v === undefined ? '' : v).trim()
    if (!raw) return ''
    const runs = raw.match(/\d[\d\s-]*\d|\d/g) || []
    if (!runs.length) return ''
    const digits = (s) => s.replace(/\D/g, '').length
    return runs
      .map((s) => s.trim())
      .sort((a, b) => digits(b) - digits(a))[0]
  }
  const cardDigits =
    cardNumberOnly(info.cardNo)  
  const asTyped = (v) =>
    String(v === null || v === undefined ? '' : v).replace(/\s+/g, ' ').trim()
  const gpayText = asTyped(info.gpayNo)
  // Shrinks for long entries so a typed name + number still fits on one line.
  // Top size matches the .gpay-value default (15px) — see the style block.
  const gpayFont = gpayText.length > 26 ? 11 : gpayText.length > 18 ? 13 : 15
  // Card details print ONLY on a DEPOSIT receipt (the bill's paytype1, sent
  // through as `payType`) — both the box at the foot AND the header's
  // "Card No. :" pair. On a CASH or GPAY receipt the client wants neither, even
  // when the operator typed something in the page's GPay / Card No. field.
  const isDeposit =
    String(info.payType === null || info.payType === undefined ? '' : info.payType)
      .trim()
      .toUpperCase() === 'DEPOSIT'
  const showGpay = isDeposit && !!gpayText
  const showCardNo = isDeposit && !!cardDigits
  const bookingTime = fmtTime(info.bookingTime)
  const withPhotos = items.filter((it) => it && (it.photo || it.code))
  // Same budgeting as the Booking Receipt: the photo grid costs ~0.8in a row,
  // so a long booking would run off the bottom of the sheet. Normal bookings
  // keep the model's 3-across layout; larger ones pack more per row.
  const cols = withPhotos.length > 9 ? 5 : withPhotos.length > 6 ? 4 : 3
  // Box heights trimmed (0.78/0.56/0.46 -> 0.68/0.50/0.42in): the grid is the
  // last block on the slip, so every millimetre it saves comes straight off the
  // overrun that was pushing the closing line past the bottom of the sheet.
  const photoH = withPhotos.length > 9 ? 0.42 : withPhotos.length > 6 ? 0.5 : 0.68
  // The slip always carries the grid of 6 boxes (2 rows x 3) from the client's
  // stationery, however many products the booking has — staff write on the
  // spare ones, and a receipt that printed no grid at all read as a broken
  // layout. Past 6 items the grid grows by whole rows so it never ends ragged.
  const cellCount = Math.max(6, Math.ceil(withPhotos.length / cols) * cols)
  // Embed each photo as a same-origin data URI; fall back to a plain <img>.
  const withUris = await Promise.all(
    withPhotos.map((it) => (it.photo ? toDataUri(it.photo) : Promise.resolve('')))
  )
  let photoCells = ''
  for (let i = 0; i < cellCount; i++) {
    const it = withPhotos[i]
    // Spare boxes keep a &nbsp; code strip so every cell is the same height
    // and the empty ones stay square with the filled ones.
    const src = it ? (withUris[i] || (it.photo ? abs(it.photo) : '')) : ''
    photoCells +=
      '<div class="pgc"><div class="pgh">' + (it ? esc(it.code) : '&nbsp;') + '</div><div class="pgp">' +
      (src ? '<img src="' + esc(src) + '" onerror="this.style.display=&#39;none&#39;">' : '') +
      '</div></div>'
  }

  const advRow = (label, value) =>
    '<tr><td class="al">' + label + '</td><td class="ac">:</td><td class="av">' + money(value) + '</td></tr>'

  const html =
    '<!doctype html><html><head><meta charset="utf-8"><title>Delivery Receipt</title>' +
    '<style>' +
    '*{box-sizing:border-box}' +
    // Body padding is deliberately NOT reset here: the injected PAGE_BILL_STYLE
    // owns it (that is where the paper margin lives) and this rule would
    // otherwise be an !important tie decided only by injection order.
    'html{margin:0!important;padding:0!important}' +
    'body{margin:0!important;font-family:Arial,Helvetica,sans-serif;color:#000;font-size:10px}' +
    // No top padding: the shared PAGE_BILL_STYLE already leaves BILL_TOP_OFFSET's
    // top gap at the top of the sheet (as body padding). Side/bottom padding
    // stays as inner breathing room inside the slip block that style pins on
    // body>*.
    '.slip{width:100%;padding:0 0.15in 0.04in;margin:0}' +
    '.lg img{display:block;margin:0 auto}' +
    '.lg{text-align:center;margin:0 0 1px}' +
    // Logo/title/date sizes are kept IDENTICAL to the Booking Receipt above —
    // the two slips print back to back and the delivery one read noticeably
    // larger when it had its own (bigger) values.
    '.lg img{height:0.3in}' +
    '.co{font-size:15px;font-weight:bold;text-align:center;letter-spacing:1px}' +
    '.brn{font-size:11px;font-weight:bold;text-align:center;margin-bottom:2px}' +
    '.adr{font-size:8.5px;font-weight:bold;text-align:center;margin:0}' +
    '.ttl{font-size:11.5px;font-weight:bold;text-align:center;margin:1px 0 6px}' +
    // Gap between the bordered boxes — see the Booking Receipt for the budget.
    // 14px -> 8px: this slip carries one more block than the Booking Receipt
    // (the G-Pay box) plus a taller summary, and at 14px the stack finished
    // below the printable area of the 21cm bill block.
    '.bx{border:1.5px solid #000;padding:3px 6px;margin:0 0 8px}' +
    'table{width:100%;border-collapse:collapse}' +
    // Each info row is its OWN flex container, so a label's width is set by
    // its own text only — not by the widest label anywhere else in the box.
    // A shared <table> column (the old .inf) sizes to whatever the widest
    // label across ALL rows needs (here, "Booking No. :"), so short labels
    // like "Name :" sat inside a column much wider than themselves, reading
    // as a big empty gap before the value. Row content that's still too long
    // (a long salesman name) now wraps onto a second line INSIDE the box
    // instead of forcing the whole row wider than the box border.
    '.ifr{display:flex;flex-wrap:wrap;align-items:baseline;padding:2px 0}' +
    '.ifr .l{font-weight:900;font-size:10px;white-space:nowrap;padding-right:4px}' +
    '.ifr .v{font-weight:900;font-size:10px;padding-right:10px;word-break:break-word}' +
    '.ifr .v.ic{white-space:normal;line-height:1.25;flex:1 1 100%;padding-right:0}' +
    '.icode{font-weight:bold;font-size:10.5px;margin:0 0 2px}' +
    '.dts td{padding:2px 0;font-size:10.5px;font-weight:900}' +
    '.chk{font-weight:bold;text-align:center;margin:4px 0 0;font-size:10px}' +
    '.sm td{padding:3px 2px;font-size:11px}' +
    '.sm .al{font-weight:900;white-space:nowrap;width:52%}' +
    '.sm .ac{width:4%;font-weight:900}' +
    '.sm .av{font-weight:900;text-align:center}' +
    '.tpv{font-weight:900;text-align:center;padding:2px 0 1px}' +
    '.gpay{font-weight:bold;text-align:center;border:2px solid #000;padding:4px 8px;margin-top:4px;display:block}' +
    '.gpay .gpay-label{font-size:10px;font-weight:800;margin-bottom:2px}' +
    // Capped at the slip's largest type (the company name, 15px) so the box
    // still stands out without dwarfing the rest of the receipt.
    '.gpay .gpay-value{font-size:15px;font-weight:900;letter-spacing:1.2px;line-height:1.15;word-break:break-word}' +
    '.pg{display:flex;flex-wrap:wrap;gap:4px;margin-top:4px}' +
    '.pgc{width:calc(' + (100 / cols).toFixed(2) + '% - 4px);page-break-inside:avoid;break-inside:avoid}' +
    '.pgh{border:1.5px solid #000;border-bottom:none;font-weight:bold;text-align:center;padding:1px 0;font-size:' + (cols > 3 ? 8.5 : 9.5) + 'px}' +
    '.pgp{border:1.5px solid #000;height:' + photoH + 'in;display:flex;align-items:center;justify-content:center;overflow:hidden}' +
    '.pgp img{max-width:100%;max-height:100%}' +
    '</style></head><body><div class="slip">' +
    '<div class="lg"><img src="' + POPIN_LOGO + '"></div>' +
    '<div class="co">' + esc(coname || 'POPIN') + '</div>' +
    '<div class="brn">' + contact + '</div>' +
    '<div class="ttl">Delivery Receipt</div>' +
    '<div class="bx">' +
    // Booking date + time live in ONE row (short "Date :" label, not "Booking
    // Date & Time :" — that wording alone was wide enough to wrap the row onto
    // two lines). There is no second date row below: it only repeated this
    // same booking date under a "Delivery Date" label the client didn't want.
    '<div class="ifr"><span class="l">Booking No. :</span><span class="v">' + esc(info.no) + '</span><span class="l">Date :</span><span class="v">' +
    esc(info.date) + (bookingTime ? ', ' + esc(bookingTime) : '') + '</span></div>' +
    '<div class="ifr"><span class="l">Name :</span><span class="v">' + esc(info.name) + '</span><span class="l">Mobile :</span><span class="v">' + esc(info.mobile) + '</span></div>' +
    // Salesman shares this row with Card No., so a non-DEPOSIT receipt drops
    // just the card label + value — the row itself stays and Salesman slides
    // to the front of it (.ifr is a flex row, so nothing else needs adjusting).
    '<div class="ifr">' +
    (showCardNo ? '<span class="l">Card No. :</span><span class="v">' + esc(cardDigits) + '</span>' : '') +
    '<span class="l">Salesman :</span><span class="v">' + esc(info.salesman) + '</span></div>' +
    // Item codes get the full width of the box and wrap onto as many lines as
    // they need — on one fixed-width line a booking past 2-3 items lost the
    // rest of its codes off the edge.
    (itemCodes ? '<div class="ifr"><span class="l">Item Code :</span><span class="v ic">' + esc(itemCodes) + '</span></div>' : '') +
    '</div>' +
    // Delivery / Return dates — larger font (per client).
    '<table class="dts" style="margin:0 0 4px">' +
    '<tr><td>D. Date : ' + esc(info.deliveryDate) + '</td><td style="text-align:right">Time : ' + esc(fmtTime(info.deliveryTime) || '2:00') + '</td></tr>' +
    '<tr><td>R. Date : ' + esc(info.returnDate) + '</td><td style="text-align:right">Time : ' + esc(fmtTime(info.returnTime) || '2:00') + '</td></tr>' +
    '</table>' +
    '<div class="bx"><table class="sm">' +
    advRow('Booking Advance', info.bookingAdvance) +
    advRow('Delivery Advance', info.deliveryAdvance) +
   
    advRow('Total', info.total) +
    '</table></div>' +
    '<div class="bx"><table class="sm">' +
    // Only shown when the booking actually carries a discount, so an
    // undiscounted bill keeps the client's original five-row block.
    (Number(info.rentDiscount) ? advRow('Rental Discount', info.rentDiscount) : '') +
    advRow('Less Total Rent', info.lessTotalRent) +
    advRow('Total Refund', info.totalRefund) +
    '</table>' +
    (info.productValue !== undefined && info.productValue !== null && String(info.productValue) !== ''
      ? '<div class="tpv">TOTAL PRODUCT VALUE : Rs. ' + money(info.productValue) + '</div>' : '') +
    // Exactly as typed on the receipt page — no cleaning of any kind.
    (showGpay
      ? '<div class="gpay"><div class="gpay-label">GPAY DEPOSIT</div><div class="gpay-value" style="font-size:' + gpayFont + 'px">' + esc(gpayText) + '</div></div>'
      : '') +
    '</div>' +
    '<div class="pg">' + photoCells + '</div>' +
    // Check-fitting note moved to the BOTTOM (per client).
    '<div class="chk">PLEASE CHECK FITTING OF CLOTH, PANT AND BLOUSE AT DELIVERY</div>' +
    '</div></body></html>'

  await printHtmlDocument(html, "Delivery Receipt", "bill")
}


// Company/branch contact details for the slip headers (address, phone,
// email) — fetched once from the branch master and cached for the session.
let COMPANY_HEADER = null
async function getCompanyHeader() {
  if (COMPANY_HEADER) return COMPANY_HEADER
  const out = { address: '', phone: '', email: '' }
  try {
    const { data } = await window.axios.get('/api/admin/rental/getbranchsingle')
    const rows = Array.isArray(data) ? data : data ? [data] : []
    let ses = {}
    try { ses = JSON.parse(localStorage.getItem('popin_session') || 'null') || {} } catch (_) { /* ignore */ }
    const bc = ses.branchcode || ses.Branch_Code || ses.loc
    const row = rows.find((r) => String(r.Code) === String(bc)) || rows[0] || {}
    // braddress is the branch's printable postal address. `address` is a
    // different column that holds a run-together blob for the digital card —
    // name, address, phone and a popindigitalcard URL all concatenated — which
    // is unusable on a printed slip. Prefer braddress, keep `address` only as a
    // last resort for branches that have not filled it in.
    out.address = row.braddress || row.brAddress || row.Address || row.address || ''
    out.phone = row.Mobile || row.mobile || row.Telephone || row.telephone || row.Phone || row.phone || ''
    out.email = row.email || row.Email || ''
  } catch (_) { /* slips print without the contact lines */ }
  COMPANY_HEADER = out
  return out
}

// ── MTO Job Work slips ───────────────────────────────────────────────────────
// Client-composed slips following the client's "NEW BILL SLIP 2026" model:
// the Job Sheet (JOB MEASUREMENT, mode 'jobsheet') and the Finish (FINISH JOB,
// mode 'finish') share one design, printed on the 8cm x 21cm slip paper.
// Company/branch names for the header come from the stored login session.
export async function printJobWorkSlip(info = {}) {
  const esc = (v) =>
    String(v === null || v === undefined ? '' : v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  // Relative server paths must be absolutified — the PRINT WebView is a
  // separate native view with no app session, so photos must come from the
  // public image hosts: /Pictures/* is served by the popin. subdomain and
  // /mto/* by www (same hosts the app itself displays photos from).
  // Every host a photo path could be served from, most-likely first — see
  // pictureUrlCandidates(): /Pictures/ spans two hosts and the measurement
  // photos are the ones on MEDIA_HOST.
  const absList = (u) => {
    if (!u) return []
    u = String(u)
    if (u.startsWith('data:') || u.startsWith('http://') || u.startsWith('https://')) return [u]
    if (!u.startsWith('/')) u = '/' + u
    if (u.toLowerCase().startsWith('/pictures/')) return pictureUrlCandidates(u)
    return [MEDIA_HOST + u]
  }
  let coname = ''
  let branchname = ''
  try {
    const s = JSON.parse(localStorage.getItem('popin_session') || 'null') || {}
    coname = s.coname || s.Description || ''
    branchname = s.branchname || s.branch_name || ''
  } catch (_) { /* no session — header shows the company name only */ }

  const hdr = await getCompanyHeader()
  const contact =
    (hdr.address ? '<div class="adr">' + esc(hdr.address) + '</div>' : '') +
    (hdr.phone || hdr.email
      ? '<div class="adr">' +
        [hdr.phone ? 'Ph. : ' + esc(hdr.phone) : '', hdr.email ? 'Email : ' + esc(hdr.email) : '']
          .filter(Boolean)
          .join(' | ') +
        '</div>'
      : '')

  const isFinish = info.mode === 'finish'

  // All job slips should render from one canonical measurement snapshot.
  // Different screens/endpoints can pass object, array, nested {data}, or
  // numeric-key objects; normalize and pick the most recent non-empty record.
  const asObj = (v) => (v && typeof v === 'object' ? v : {})
  const toRecords = (payload) => {
    if (!payload) return []
    if (Array.isArray(payload)) return payload.filter((r) => r && typeof r === 'object')
    const o = asObj(payload)
    if (Array.isArray(o.data)) return o.data.filter((r) => r && typeof r === 'object')
    if (o.data && typeof o.data === 'object') return [o.data]
    const keyed = Object.keys(o)
      .filter((k) => /^\d+$/.test(k) && o[k] && typeof o[k] === 'object')
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => o[k])
    if (keyed.length) return keyed
    return [o]
  }
  const hasMeasurementValues = (r) => {
    if (!r || typeof r !== 'object') return false
    const keys = [
      'suit_lengha', 'sherwani', 'kurta', 'jeckat', 'solder', 'sleev',
      'shev_bottom', 'cheast', 'stomach', 'hips', 'neck_color', 'armod',
      'pant_lengha', 'payjama', 'churidar', 'pathani', 'waist', 'hip',
      'thighs', 'knee', 'bottom', 'corch', 'full_round', 'inside_lenght',
    ]
    return keys.some((k) => r[k] !== undefined && r[k] !== null && String(r[k]).trim() !== '')
  }
  const recordSortKey = (r) => {
    const t = Date.parse(
      r.updated_at || r.updatedAt || r.modified_at || r.modifiedAt ||
      r.created_at || r.createdAt || ''
    )
    if (!Number.isNaN(t) && t > 0) return t
    const n = Number(r.id || r.srno || r.sr_no || 0)
    return Number.isFinite(n) ? n : 0
  }
  const pickCanonicalMeasurement = () => {
    const preferred = asObj(info.measurement)
    if (hasMeasurementValues(preferred)) return preferred
    const recs = toRecords(info.measurements)
    if (!recs.length) return preferred
    const withValues = recs.filter(hasMeasurementValues)
    const pool = withValues.length ? withValues : recs
    return pool.sort((a, b) => recordSortKey(a) - recordSortKey(b)).slice(-1)[0] || {}
  }
  const m = pickCanonicalMeasurement()

  const detailRow = (label, value) =>
    '<tr><td class="dl">' + label + '</td><td class="dc">:</td><td class="dv">' +
    esc(value) + '</td></tr>'

  const sizeGrid = (names, vals) =>
    '<table class="sz"><tr>' +
    names.map((n) => '<th>' + n + '</th>').join('') +
    '</tr><tr>' +
    vals.map((v) => '<td>' + esc(v) + '</td>').join('') +
    '</tr></table>'

  // A pair with an empty right-hand label (e.g. REMARK with no VOICE field)
  // spans the row instead of drawing an empty second box.
  const pairRows = (pairs) =>
    pairs
      .map((p) =>
        p[2]
          ? '<tr><td class="ml">' + p[0] + '</td><td class="mv">' + esc(p[1]) +
            '</td><td class="ml">' + p[2] + '</td><td class="mv">' + esc(p[3]) +
            '</td></tr>'
          : '<tr><td class="ml">' + p[0] + '</td><td class="mv" colspan="3">' + esc(p[1]) +
            '</td></tr>'
      )
      .join('')

  // Which measurement grids to show: exactly what the measurement SCREENS
  // show, so the slip is a faithful print of the form that was filled in.
  // The four screens (customermeasurment / …finish / …urgent / …jobworker) all
  // use `categorytype === 'Top' || 'Both' || !categorytype` for the top block
  // and `categorytype === 'Bottom' || 'Both'` for the bottom one.
  //
  // The bottom grid used to carry an extra fallback — when the category was
  // unknown it printed anyway if any bottom field happened to hold a value.
  // That made the slip disagree with the screen: a blank category shows ONLY
  // the top block on screen, so stray bottom data (left over from a category
  // change, or written by the old desktop system) printed a whole second
  // measurement column the operator never filled in and could not even see.
  const ct = String(info.categorytype || '').toLowerCase()
  const hasTopValues = [
    m.suit_lengha, m.sherwani, m.kurta, m.jeckat, m.solder, m.sleev,
    m.shev_bottom, m.cheast, m.stomach, m.hips, m.neck_color, m.armod,
    m.top_remark,
  ].some((v) => v !== undefined && v !== null && String(v).trim() !== '')
  const hasBottomValues = [
    m.pant_lengha, m.payjama, m.churidar, m.pathani, m.waist, m.hip,
    m.thighs, m.knee, m.bottom, m.corch, m.full_round, m.inside_lenght,
    m.bottom_remark,
  ].some((v) => v !== undefined && v !== null && String(v).trim() !== '')

  // The GARMENT decides which side prints. categorytype comes from the
  // product's category master, so a Bottom product prints the Bottom grid.
  //
  // The Tailor Master fields used to outrank it, on the theory that a job sent
  // to a top master carries top_master with bottom_master null (and the reverse
  // for a bottom job). That is not how the data behaves: the sale screen has a
  // single "Tailor Master" box that writes top_master, and bottom_master is
  // only settable from an optional field in the measurement popup that stays
  // blank — so "top_master set, bottom_master null" is the resting state of
  // every row, bottom garments included (checked against the API for both
  // garments of two bills). Letting it win printed the Top grid on every slip,
  // including a Pants job. The masters are a tie-breaker now, and stray values
  // are still the last resort.
  const topMaster = String(m.top_master || '').trim()
  const bottomMaster = String(m.bottom_master || '').trim()
  let side
  if (ct === 'top' || ct === 'bottom' || ct === 'both') side = ct
  else if (topMaster && !bottomMaster) side = 'top'
  else if (bottomMaster && !topMaster) side = 'bottom'
  else if (!hasTopValues && hasBottomValues) side = 'bottom'
  else side = 'top'
  // 'Both' prints both grids, which is what the measurement screens show for a
  // Both product — this block is meant to be a faithful print of that form.
  const showTop = side === 'top' || side === 'both'
  const showBottom = side === 'bottom' || side === 'both'

  let grids = ''
  if (showTop) {
    grids +=
      '<div class="bx">' +
      sizeGrid(['SUIT', 'SHERWANI', 'KURTA', 'JACKET'],
        [m.suit_lengha, m.sherwani, m.kurta, m.jeckat]) +
      '<table class="mt">' +
      pairRows([
        ['SHOULDER', m.solder, 'SLEEV', m.sleev],
        ['SHEV BOTTOM', m.shev_bottom, 'CHEST', m.cheast],
        ['STOMACH', m.stomach, 'HIPS', m.hips],
        ['NECK COLOR', m.neck_color, 'ARMOD', m.armod],
        ['REMARK', m.top_remark, '', ''],
      ]) +
      '</table></div>'
  }
  if (showBottom) {
    grids +=
      '<div class="bx">' +
      sizeGrid(['PANT LEHENGA', 'PAYJAMA', 'CHURIDAR', 'PATHANI'],
        [m.pant_lengha, m.payjama, m.churidar, m.pathani]) +
      '<table class="mt">' +
      pairRows([
        ['WAIST', m.waist, 'HIP', m.hip],
        ['THIGHS', m.thighs, 'KNEE', m.knee],
        ['BOTTOM', m.bottom, 'CROTCH', m.corch],
        ['FULL ROUND', m.full_round, 'INSIDE LENGTH', m.inside_lenght],
        ['REMARK', m.bottom_remark, '', ''],
      ]) +
      '</table></div>'
  }

  // Pre-fetch every photo (same-origin, cookie-bearing) and embed as a data
  // URI. If the embed fails, fall back to a plain <img> at the public URL —
  // that displays cross-origin fine (no CORS on <img>) for public images.
  // The third photo box belongs to the Finish slip, and to any Job Sheet that
  // actually carries a final photo (the urgent measurement screen has a "Final
  // Photo" box and asks for it on the slip). A caller that sends no final photo
  // still gets two boxes, so the two real photos keep half the width each.
  const showFinished = isFinish || !!info.photoFinished
  const [pProduct, pCloth, pFinished] = await Promise.all([
    toDataUri(info.photoProduct),
    toDataUri(info.photoCloth),
    showFinished ? toDataUri(info.photoFinished) : Promise.resolve(''),
  ])
  // Photo boxes stay visible (like the model's PHOTO placeholders); a photo
  // that fails to load just leaves its box empty instead of a broken icon.
  const photoBox = (label, dataUri, url) => {
    const urls = dataUri ? [dataUri] : (url ? absList(url) : [])
    const src = urls[0] || ''
    // The plain-<img> fallback (used when the data-URI prefetch failed) retries
    // the other picture host before giving up, so a photo stored on the host we
    // did not guess first still prints instead of leaving the box empty.
    const onerr = urls[1]
      ? "var a=this.getAttribute('data-alt');" +
        "if(a){this.removeAttribute('data-alt');this.src=a}" +
        "else{this.style.display='none'}"
      : "this.style.display='none'"
    return '<div class="pc"><div class="pl">' + label + '</div><div class="ph">' +
      (src
        ? '<img src="' + esc(src) + '"' +
          (urls[1] ? ' data-alt="' + esc(urls[1]) + '"' : '') +
          ' onerror="' + esc(onerr) + '">'
        : '') +
      '</div></div>'
  }
  // The third box used to be hard-wired on for every slip, so a Job Sheet —
  // which had no finished photo to pass — printed a permanently empty frame.
  // It is drawn only when there is a photo for it (see showFinished above).
  const photos = [
    photoBox('PRODUCT PHOTO', pProduct, info.photoProduct),
    photoBox('CLOTH PHOTO', pCloth, info.photoCloth),
    showFinished
      ? photoBox(isFinish ? 'FINISHED PRODUCT' : 'FINAL PHOTO', pFinished, info.photoFinished)
      : '',
  ].join('')

  const title = isFinish ? 'FINISH JOB' : 'JOB MEASUREMENT'
  // The box at the foot of the slip should show the Finish Job remark when
  // present. Different endpoints and screens use varied property names
  // (remark, remarks, note, finish_remark, receive_remark, etc.), so accept
  // any of the common spellings here. Job Sheet keeps the NOTE heading; the
  // Finish slip labels it REMARK.
  const pickFirst = (obj, keys) => {
    if (!obj) return ''
    for (const k of keys) {
      const v = obj[k]
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v)
    }
    return ''
  }
  const remarkKeys = [
    'remark', 'remarks', 'Remark', 'finish_remark', 'receive_remark',
    'note', 'notes', 'Note', 'finishRemark', 'receiveRemark',
    'job_remark', 'jobremark', 'work_remark', 'rec_remark',
    'recremark', 'finish_note', 'finishNote', 'remark1',
  ]
  const noteText = pickFirst(info, remarkKeys) || pickFirst(m, remarkKeys) || ''
  const noteLabel = isFinish ? 'REMARK' : 'NOTE'
  const html =
    '<!doctype html><html><head><meta charset="utf-8"><title>' + title + '</title>' +
    '<style>' +
    '*{box-sizing:border-box}' +
    // Body padding is deliberately NOT reset here: the injected PAGE_BILL_STYLE
    // owns it (that is where the paper margin lives) and this rule would
    // otherwise be an !important tie decided only by injection order.
    'html{margin:0!important;padding:0!important}' +
    'body{margin:0!important;font-family:Arial,Helvetica,sans-serif;color:#000;font-size:10px}' +
    // No top margin at all — the logo is flush with the top of the page.
    '.slip{width:100%;padding:0 0.15in 0.05in;margin:0}' +
    '.lg{text-align:center;margin:0 0 2px;padding:0}' +
    '.lg img{display:block;margin:0 auto;height:0.4in}' +
    '.co{font-size:15px;font-weight:bold;text-align:center;letter-spacing:1px}' +
    '.brn{font-size:10px;font-weight:bold;text-align:center;margin-bottom:2px}' +
    '.adr{font-size:8.5px;font-weight:bold;text-align:center;margin:0}' +
    '.ttl{font-size:13px;font-weight:bold;text-align:center;letter-spacing:2px;margin:1px 0 3px}' +
    '.bx{border:1.5px solid #000;border-radius:8px;padding:3px 6px;margin:0 0 2px}' +
    '.jw{font-size:12.5px;font-weight:bold}' +
    'table{width:100%;border-collapse:collapse}' +
    '.dt td{padding:1.5px 0;font-size:11px}' +
    '.dl{font-weight:bold;width:37%;white-space:nowrap}' +
    '.dc{width:4%}' +
    '.dv{font-weight:bold}' +
    '.sz{margin-bottom:3px;table-layout:fixed}' +
    '.sz th{font-size:11px;line-height:1.05;font-weight:bold;border:1px solid #000;padding:3px 1px;text-align:center;vertical-align:middle}' +
    '.sz td{border:1px solid #000;height:22px;padding:2px 2px;text-align:center;font-weight:bold;font-size:11px;line-height:1.1;word-break:break-word;white-space:normal;vertical-align:middle;overflow:hidden}' +
    '.mt{table-layout:fixed}' +
    '.mt td{padding:2px 0;vertical-align:middle}' +
    '.ml{font-size:10px;font-weight:bold;white-space:nowrap;padding-right:5px;width:28%}' +
    '.mv{border:1px solid #000;height:22px;width:22%;text-align:center;vertical-align:middle;font-weight:bold;font-size:11px;line-height:1.1;box-sizing:border-box;word-break:break-word;white-space:normal;padding:2px 3px;overflow:hidden}' +
    '.rem{display:flex;align-items:center;gap:5px;margin:0 0 1px}' +
    '.rem span{font-size:10.5px;font-weight:bold;white-space:nowrap}' +
    // Remark: single line — fixed height, no wrapping (overflow trimmed).
    '.rembox{border:1.5px solid #000;height:18px;flex:1;padding:2px 5px;font-weight:bold;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
    '.notebox{height:auto;min-height:0.24in;white-space:normal}' +
    '.pr{display:flex;gap:6px;margin:1px 0 2px}' +
    '.pc{flex:1;min-width:0}' +
    '.pl{font-size:8.5px;font-weight:bold;text-align:center;margin-bottom:2px}' +
    '.ph{border:1.5px solid #000;height:1.08in;display:flex;align-items:center;justify-content:center;overflow:hidden}' +
    '.ph img{max-width:100%;max-height:100%}' +
    '.ft{margin:0 0 3px}' +
    '.fl{font-size:9px;font-weight:bold;white-space:nowrap;padding-right:3px}' +
    '.fv{border:1.5px solid #000;height:22px;width:26%}' +
    '.fv.mp{text-align:center;font-weight:bold;font-size:11px}' +
    '</style></head><body><div class="slip">' +
    '<div class="lg"><img src="' + POPIN_LOGO + '"></div>' +
    '<div class="co">' + esc(coname || 'POPIN') + '</div>' +
    '<div class="brn">' + contact + '</div>' +
    '<div class="ttl">' + title + '</div>' +
    '<div class="bx jw">JOB WORK : ' + esc(info.jobworkName) + '</div>' +
    '<div class="bx"><table class="dt">' +
    detailRow('Customer Name', info.customerName) +
    detailRow('Order No.', info.orderNo) +
    detailRow('Product Name', info.productName) +
    // Pick-up Date is optional: the MTO measurement screen prints a slip
    // without it (the label alone next to an empty box read as a missing
    // date), while the job-worker / urgent / finish slips still carry it.
    (String(info.pickupDate || '').trim()
      ? detailRow('Pick-up Date', info.pickupDate)
      : '') +
    // Receive Date is optional the same way: the urgent job sheet prints it
    // (the date the job came back), the other slips send nothing and the row
    // is dropped.
    (String(info.receiveDate || '').trim()
      ? detailRow('Receive Date', info.receiveDate)
      : '') +
    (isFinish ? detailRow('Return Date', info.returnDate) : '') +
    '</table></div>' +
    grids +
    '<div class="pr">' + photos + '</div>' +
    (isFinish
      ? '<table class="ft"><tr><td class="fl">MAKING PRICE :</td><td class="fv mp">' +
        esc(info.makingPrice) + '</td><td style="width:6%"></td>' +
        '<td class="fl">SIGN. &amp; STAMP</td><td class="fv"></td></tr></table>'
      : '') +
    '<div class="rem"><span>' + noteLabel + '</span><div class="rembox notebox">' + esc(noteText) + '</div></div>' +
    '</div></body></html>'

  await printHtmlDocument(html, isFinish ? 'Finish Job Slip' : 'Job Sheet Slip', 'bill')
}

// ── GST Tax Invoice (A4) ─────────────────────────────────────────────────────
// Client-composed replacement for the server print page
// (/admin/rental/gstinvoice/print/{id}), which the app used to open through
// printServerHtml. Composing it here means the print no longer depends on a
// Blade route, behaves the same on web and inside the native WebView, and can
// be restyled without a backend deploy. The layout deliberately mirrors that
// Blade view so the printed document is the one the office already knows.
//
// `info` shape (built by gstinvoice.vue -> buildGstInvoiceInfo):
//   company  {name, address, phone, email, gstin, state, stateCode}
//   invoice  {no, date, deliveryDate, returnDate, bookingNo, salesman}
//   customer {name, address[], city, pin, mobile, gstin, cardNo}
//   items[]  {code, description, size, qty, rate, discPerc, taxable,
//             gstPerc, gstAmt, amount, hsn,
//             cgstPerc, cgstAmt, sgstPerc, sgstAmt, igstPerc, igstAmt}
//   totals   {taxable, discount, cgst, sgst, igst, roundoff, grandTotal}
// Builds the markup + stylesheet once, so the Print button and the Download PDF
// button can never drift apart — both render this exact document.
function gstInvoiceDocument(info = {}) {
  const esc = (v) =>
    String(v === null || v === undefined ? '' : v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  const nd = (v, places) => {
    const n = Number(v)
    return (isNaN(n) ? 0 : n).toFixed(places)
  }
  const n2 = (v) => nd(v, 2)
  const n1 = (v) => nd(v, 1)

  const company = info.company || {}
  const invoice = info.invoice || {}
  const customer = info.customer || {}
  const items = Array.isArray(info.items) ? info.items : []
  const totals = info.totals || {}

  // Item rows, padded out to 3 like the Blade view so a one-line invoice
  // doesn't leave the table looking cut off.
  let rows = ''
  items.forEach((it, i) => {
    rows +=
      '<tr>' +
      '<td class="c">' + (i + 1) + '</td>' +
      '<td class="c">' + esc(it.code) + '</td>' +
      '<td>' + esc(it.description) + '</td>' +
      '<td class="c">' + esc(it.size) + '</td>' +
      '<td class="c">' + esc(it.qty) + '</td>' +
      '<td class="n">' + n2(it.rate) + '</td>' +
      '<td class="c">' + n1(it.discPerc) + '%</td>' +
      '<td class="n">' + n2(it.taxable) + '</td>' +
      '<td class="c">' + n1(it.gstPerc) + '%</td>' +
      '<td class="n">' + n2(it.gstAmt) + '</td>' +
      '<td class="n">' + n2(it.amount) + '</td>' +
      '</tr>'
  })
  for (let i = items.length; i < 3; i++) {
    rows += '<tr class="pad"><td colspan="11">&nbsp;</td></tr>'
  }

  const totalRow = (label, value, cls) =>
    '<tr' + (cls ? ' class="' + cls + '"' : '') + '>' +
    '<td class="tl">' + esc(label) + '</td>' +
    '<td class="tv">' + n2(value) + '</td></tr>'
  // Only tax lines that actually carry a value are printed — plenty of these
  // rental invoices have no GST applied at all.
  const opt = (label, value) => (Number(value) ? totalRow(label, value) : '')

  const gstTotal =
    (Number(totals.cgst) || 0) + (Number(totals.sgst) || 0) + (Number(totals.igst) || 0)

  const totalsHtml =
    totalRow('Taxable Amount:', totals.taxable) +
    opt('Discount:', totals.discount) +
    opt('CGST:', totals.cgst) +
    opt('SGST:', totals.sgst) +
    opt('IGST:', totals.igst) +
    (gstTotal ? totalRow('Total GST:', gstTotal) : '') +
    opt('Round Off:', totals.roundoff) +
    totalRow('GRAND TOTAL:', totals.grandTotal, 'grand')

  // ── GST SUMMARY ───────────────────────────────────────────────────────────
  // The rate-wise breakup the sheet carries under the totals: one line per
  // HSN code + GST rate, with its taxable value and the three tax components.
  // Grouping by HSN alone merged every line into one row when the HSN was
  // blank (as on most rental invoices) and showed the highest rate for all of
  // them, so a 5% + 18% invoice printed a single 9%/9% row.
  const summary = []
  items.forEach((it) => {
    const hsn = String(it.hsn === null || it.hsn === undefined ? '' : it.hsn).trim()
    const cgstPerc = Number(it.cgstPerc) || 0
    const sgstPerc = Number(it.sgstPerc) || 0
    const igstPerc = Number(it.igstPerc) || 0
    const key = hsn + '|' + cgstPerc + '|' + sgstPerc + '|' + igstPerc
    let row = summary.find((r) => r.key === key)
    if (!row) {
      row = { key, hsn, taxable: 0, cgstPerc, cgst: 0, sgstPerc, sgst: 0, igstPerc, igst: 0 }
      summary.push(row)
    }
    row.taxable += Number(it.taxable) || 0
    row.cgst += Number(it.cgstAmt) || 0
    row.sgst += Number(it.sgstAmt) || 0
    row.igst += Number(it.igstAmt) || 0
  })
  // Lowest rate first (5% before 18%), then by HSN.
  summary.sort(
    (a, b) =>
      a.cgstPerc + a.sgstPerc + a.igstPerc - (b.cgstPerc + b.sgstPerc + b.igstPerc) ||
      a.hsn.localeCompare(b.hsn)
  )

  const summaryRows = summary
    .map(
      (r) =>
        '<tr>' +
        '<td class="c">' + (esc(r.hsn) || '-') + '</td>' +
        '<td class="c">' + n1(r.cgstPerc + r.sgstPerc + r.igstPerc) + '%</td>' +
        '<td class="n">' + n2(r.taxable) + '</td>' +
        '<td class="c">' + n1(r.cgstPerc) + '%</td>' +
        '<td class="n">' + n2(r.cgst) + '</td>' +
        '<td class="c">' + n1(r.sgstPerc) + '%</td>' +
        '<td class="n">' + n2(r.sgst) + '</td>' +
        '<td class="c">' + n1(r.igstPerc) + '%</td>' +
        '<td class="n">' + n2(r.igst) + '</td>' +
        '</tr>'
    )
    .join('')

  // The total line takes the invoice totals, not the sum of the rows above, so
  // this table can never disagree with the totals box beside it. The rate cells
  // are dashes — a total of two different rates would be meaningless.
  const summaryHtml =
    '<div class="sh">GST SUMMARY</div>' +
    '<table class="sum">' +
    '<thead><tr>' +
    '<th style="width:10%">HSN Code</th>' +
    '<th style="width:8%">GST %</th>' +
    '<th style="width:16%">Taxable Value</th>' +
    '<th style="width:9%">CGST Rate</th>' +
    '<th style="width:13%">CGST Amount</th>' +
    '<th style="width:9%">SGST Rate</th>' +
    '<th style="width:13%">SGST Amount</th>' +
    '<th style="width:9%">IGST Rate</th>' +
    '<th style="width:13%">IGST Amount</th>' +
    '</tr></thead><tbody>' + summaryRows + '</tbody>' +
    '<tfoot><tr>' +
    '<td class="c"><b>TOTAL</b></td>' +
    '<td class="c">-</td>' +
    '<td class="n"><b>' + n2(totals.taxable) + '</b></td>' +
    '<td class="c">-</td>' +
    '<td class="n"><b>' + n2(totals.cgst) + '</b></td>' +
    '<td class="c">-</td>' +
    '<td class="n"><b>' + n2(totals.sgst) + '</b></td>' +
    '<td class="c">-</td>' +
    '<td class="n"><b>' + n2(totals.igst) + '</b></td>' +
    '</tr></tfoot></table>'

  // ── Terms & Conditions ────────────────────────────────────────────────────
  const TERMS = [
    'All rentals are subject to availability and confirmation.',
    'Security deposit will be refunded after the safe return of items.',
    'Any damage or loss will be charged as per company policy.',
    'Items must be returned in the same condition as delivered.',
    'Late return charges will be applicable as per policy.',
  ]
  const termsHtml =
    '<div class="terms">' +
    '<div class="th">Terms &amp; Conditions:</div>' +
    TERMS.map((t, i) => '<div class="tr">' + (i + 1) + '. ' + esc(t) + '</div>').join('') +
    '</div>' +
    '<div class="ty">Thank you for choosing ' + esc(company.name || 'POPIN') + '!' +
    (company.phone ? '<br>For any queries, please contact: ' + esc(company.phone) : '') +
    '</div>'

  const companyLine2 = [
    company.phone ? 'Phone: ' + esc(company.phone) : '',
    company.email ? 'Email: ' + esc(company.email) : '',
  ].filter(Boolean).join(' | ')
  // Registration line: the company GSTIN, then the place of supply. The legacy
  // VAT/CST registrations are deliberately not printed — see gstinvoice.vue for
  // where the number comes from. The state code is the first two digits of the
  // GSTIN (27 = Maharashtra), so it is derived rather than stored twice.
  const gstinPrefix = String(company.gstin || '').trim().slice(0, 2)
  const stateCode =
    company.stateCode || (/^\d{2}$/.test(gstinPrefix) ? gstinPrefix : '')
  const stateBit = company.state
    ? 'State: ' + esc(company.state) + (stateCode ? ' &nbsp;State Code : ' + esc(stateCode) : '')
    : stateCode ? 'State Code : ' + esc(stateCode) : ''
  const companyLine3 = [
    company.gstin ? 'GSTIN: ' + esc(company.gstin) : '',
    stateBit,
  ].filter(Boolean).join(' | ')

  const addressLines = (Array.isArray(customer.address) ? customer.address : [customer.address])
    .filter(Boolean)
    .map((l) => esc(l) + '<br>')
    .join('')

  const html =
    '<div class="inv">' +

    '<div class="hd">' +
    '<div class="lg"><img src="' + POPIN_LOGO + '"></div>' +
    '<div class="co">' + esc(company.name || 'POPIN') + '</div>' +
    (company.address ? '<div class="cd">' + esc(company.address) + '</div>' : '') +
    (companyLine2 ? '<div class="cd">' + companyLine2 + '</div>' : '') +
    (companyLine3 ? '<div class="cd">' + companyLine3 + '</div>' : '') +
    '<div class="ttl">TAX INVOICE</div>' +
    '</div>' +

    '<div class="info">' +
    '<div class="bx">' +
    '<div class="it">BILL TO:</div>' +
    '<b>' + esc(customer.name) + '</b><br>' +
    addressLines +
    ((customer.city || customer.pin)
      ? esc(customer.city) + (customer.pin ? ' - ' + esc(customer.pin) : '') + '<br>'
      : '') +
    (customer.mobile ? 'Phone: ' + esc(customer.mobile) + '<br>' : '') +
    (customer.gstin ? 'GSTIN: ' + esc(customer.gstin) + '<br>' : '') +
    (customer.cardNo ? 'Card No: ' + esc(customer.cardNo) : '') +
    '</div>' +
    '<div class="bx">' +
    '<div class="it">INVOICE DETAILS:</div>' +
    '<b>Invoice No:</b> ' + esc(invoice.no) + '<br>' +
    '<b>Invoice Date:</b> ' + esc(invoice.date) + '<br>' +
    (invoice.deliveryDate ? '<b>Delivery Date:</b> ' + esc(invoice.deliveryDate) + '<br>' : '') +
    (invoice.returnDate ? '<b>Return Date:</b> ' + esc(invoice.returnDate) + '<br>' : '') +
    (invoice.bookingNo ? '<b>Booking No:</b> ' + esc(invoice.bookingNo) + '<br>' : '') +
    (invoice.salesman ? '<b>Salesman:</b> ' + esc(invoice.salesman) : '') +
    '</div>' +
    '</div>' +

    '<table class="items">' +
    '<thead><tr>' +
    '<th style="width:4%">S.No</th>' +
    '<th style="width:12%">Product Code</th>' +
    '<th style="width:20%">Description</th>' +
    '<th style="width:6%">Size</th>' +
    '<th style="width:5%">Qty</th>' +
    '<th style="width:9%">Rate</th>' +
    '<th style="width:6%">Disc%</th>' +
    '<th style="width:9%">Taxable</th>' +
    '<th style="width:6%">GST%</th>' +
    '<th style="width:9%">GST Amt</th>' +
    '<th style="width:10%">Total</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table>' +

    '<div class="tot"><table class="tt">' + totalsHtml + '</table></div>' +

    '<div class="words"><b>Amount in Words:</b> ' + esc(rupeesInWords(totals.grandTotal)) + '</div>' +

    summaryHtml +

    termsHtml +

    '<div class="sig">' +
    '<div>Customer Signature</div>' +
    '<div>Authorized Signature</div>' +
    '<div>Company Seal</div>' +
    '</div>' +

    '</div>'

  const css =
    '<style>' +
    '@page{size:A4 portrait;margin:' + PAGE_MARGIN + '}' +
    '*{margin:0;padding:0;box-sizing:border-box}' +
    'body{font-family:Arial,Helvetica,sans-serif;font-size:9px;line-height:1.2;color:#000;' +
    '-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
    '.inv{width:100%}' +
    '.hd{text-align:center;border-bottom:1px solid #000;padding-bottom:5px;margin-bottom:8px}' +
    '.lg img{max-height:40px;max-width:120px;object-fit:contain}' +
    '.co{font-size:14px;font-weight:bold;margin-bottom:2px}' +
    '.cd{font-size:8px;line-height:1.25}' +
    '.ttl{font-size:12px;font-weight:bold;margin-top:3px}' +
    '.info{display:flex;justify-content:space-between;margin-bottom:8px;font-size:8px}' +
    '.info .bx{width:48%;background:#f9f9f9;border:1px solid #ddd;padding:4px;line-height:1.35}' +
    '.it{font-weight:bold;text-decoration:underline;font-size:9px;margin-bottom:2px}' +
    '.items{width:100%;border-collapse:collapse;margin-bottom:8px;font-size:8px;table-layout:fixed}' +
    '.items th,.items td{border:1px solid #000;padding:2px 3px;text-align:left;word-wrap:break-word}' +
    '.items th{background:#e9e9e9;font-weight:bold;text-align:center;padding:3px 2px}' +
    '.items td.c{text-align:center}.items td.n{text-align:right}' +
    '.items tr.pad td{height:15px}' +
    // Keep a row whole across a page break and repeat the headings.
    '.items tr{page-break-inside:avoid;break-inside:avoid}' +
    '.items thead{display:table-header-group}' +
    '.tot{display:flex;justify-content:flex-end;margin-bottom:8px}' +
    '.tt{width:45%;border-collapse:collapse;font-size:9px}' +
    '.tt td{border:1px solid #000;padding:2px 4px}' +
    '.tt .tl{font-weight:bold}' +
    '.tt .tv{text-align:right;width:45%}' +
    '.tt tr.grand td{background:#e9e9e9;font-weight:bold}' +
    '.words{border:1px solid #000;padding:4px;margin-bottom:8px;font-size:9px}' +
    // GST SUMMARY: same borders and heading tint as the item table so the two
    // read as one sheet.
    '.sh{font-size:9px;font-weight:bold;margin-bottom:3px}' +
    '.sum{width:100%;border-collapse:collapse;margin-bottom:8px;font-size:8px;table-layout:fixed}' +
    '.sum th,.sum td{border:1px solid #000;padding:2px 3px;word-wrap:break-word}' +
    '.sum th{background:#e9e9e9;font-weight:bold;text-align:center;padding:3px 2px}' +
    '.sum td.c{text-align:center}.sum td.n{text-align:right}' +
    '.sum tfoot td{background:#e9e9e9}' +
    '.sum tr{page-break-inside:avoid;break-inside:avoid}' +
    '.terms{border:1px solid #000;padding:4px;margin-bottom:6px;font-size:8px;line-height:1.35}' +
    '.terms .th{font-weight:bold;margin-bottom:2px}' +
    '.ty{text-align:center;font-size:8px;line-height:1.35;margin-bottom:4px}' +
    // Three boxes now (customer / authorised / seal), so each takes a third.
    '.sig{display:flex;justify-content:space-between;margin-top:26px;font-size:9px}' +
    '.sig div{width:30%;border-top:1px solid #000;padding-top:3px;text-align:center}' +
    '</style>'

  return { body: html, css, no: invoice.no || '' }
}

// Print the invoice (browser print dialog / native print service).
export function printGstInvoiceSlip(info = {}) {
  const doc = gstInvoiceDocument(info)
  // bodyPad is appended HERE rather than inside gstInvoiceDocument because
  // downloadGstInvoicePdf renders that same stylesheet into the live page (the
  // offscreen html2pdf host), where a `body{padding}` rule would leak onto the
  // app's own body. The PDF path gets the margin from html2pdf's own setting.
  printHtml(doc.body, 'GST Invoice ' + doc.no, doc.css + '<style>' + bodyPad() + '</style>', 'portrait')
}

// Save the SAME document as a PDF, generated on the device instead of by the
// server's /gstinvoice/pdf/{id} route. html2pdf rasterises the DOM through
// html2canvas, so the markup is laid out in a real 200mm-wide element first
// (the A4 printable width at the 0.5cm side margins PAGE_MARGIN uses) —
// otherwise it would be measured against the viewport and come out the wrong
// scale.
// Loaded dynamically to keep html2pdf out of every bundle that imports this
// module for printing alone.
export async function downloadGstInvoicePdf(info = {}, filename) {
  const doc = gstInvoiceDocument(info)
  const name = filename || 'gst-invoice-' + (doc.no || 'document') + '.pdf'

  // Off-screen (not display:none — html2canvas can't measure a hidden element).
  // The off-screen positioning lives on an outer wrapper and html2pdf is handed
  // the plain inner element: html2pdf CLONES the source (inline styles and all)
  // into its own overlay before rasterising, so a source carrying
  // `left:-10000px` is cloned off-canvas too and the PDF comes out blank.
  const host = document.createElement('div')
  host.style.cssText = 'position:fixed;left:-10000px;top:0;z-index:-1'
  const page = document.createElement('div')
  page.style.cssText = 'width:200mm;background:#fff'
  page.innerHTML = doc.css + doc.body
  host.appendChild(page)
  document.body.appendChild(host)

  try {
    const { default: html2pdf } = await import('html2pdf.js')
    const blob = await html2pdf()
      .from(page)
      .set({
        margin: [12.7, 5, 5, 5], // mm [top, right, bottom, left] — 12.7mm = the 0.5in
                                     // top every print format uses (PAGE_TOP_MARGIN)
        filename: name,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .output('blob')

    await savePdfBlob(blob, name)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('PDF generation failed', e)
  } finally {
    host.remove()
  }
}

// Hand a generated PDF to the user: a normal download on the web, the OS share
// sheet on device (same route downloadServerPdf takes, since a WebView can't
// write to the Downloads folder itself).
async function savePdfBlob(blob, filename) {
  if (!Capacitor.isNativePlatform()) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 60000)
    return
  }
  try {
    const buf = await blob.arrayBuffer()
    const b64 = bytesToBase64(new Uint8Array(buf))
    const { uri } = await Filesystem.writeFile({
      path: filename,
      data: b64,
      directory: Directory.Cache,
    })
    await Share.share({ title: filename, url: uri, dialogTitle: filename })
  } catch (e) {
    // Dismissing the share sheet rejects with a benign "canceled" error.
    if (!(e && /cancel/i.test(e.message || ''))) {
      // eslint-disable-next-line no-console
      console.error('PDF save failed', e)
    }
  }
}

// Indian numbering (Crore / Lakh / Thousand) amount in words — the JS twin of
// the numberToWords helper the Blade print view carried.
export function rupeesInWords(value) {
  const num = Math.round(Number(value) || 0)
  if (!num) return 'Zero Rupees Only'

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
    'Eighteen', 'Nineteen']
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

  // 0–99
  const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : ''))
  // 0–999
  const three = (n) => {
    const h = Math.floor(n / 100)
    const r = n % 100
    return (h ? ones[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? two(r) : '')
  }

  let rest = num
  const parts = []
  const crore = Math.floor(rest / 10000000); rest %= 10000000
  const lakh = Math.floor(rest / 100000); rest %= 100000
  const thousand = Math.floor(rest / 1000); rest %= 1000

  if (crore) parts.push(three(crore) + ' Crore')
  if (lakh) parts.push(three(lakh) + ' Lakh')
  if (thousand) parts.push(three(thousand) + ' Thousand')
  if (rest) parts.push(three(rest))

  return parts.join(' ').replace(/\s+/g, ' ').trim() + ' Rupees Only'
}
